import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import DashboardSidebar from '../../components/ui/DashboardSidebar';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { fetchStudentsInClassroom, type StudentCreatePayload, type StudentSummary } from '../../lib/classroomsApi';
import CatalogPanel from '../class-management-hub/components/CatalogPanel';
import ProfesoriPanel from '../class-management-hub/components/ProfesoriPanel';
import {
  addStudentToClassroomAsSecretariat,
  acceptSecretariatTransfer,
  archiveSecretariatClass,
  createSecretariatTransfer,
  createSecretariatGoogleSheet,
  createSecretariatTeacher,
  deleteSecretariatGoogleSheet,
  getSecretariatTransferExportUrl,
  getSecretariatTransferFileDownloadUrl,
  listAllSchools,
  listSecretariatClasses,
  listSecretariatArchivedCatalogs,
  listSecretariatDossierStudents,
  listSecretariatGoogleSheets,
  listSecretariatIncomingTransfers,
  listSecretariatTeachers,
  listSecretariatOutgoingTransfers,
  listSecretariatStudentDocuments,
  listSecretariatTransferFiles,
  rejectSecretariatTransfer,
  uploadSecretariatStudentDocument,
  deleteSecretariatStudentDocument,
  getSecretariatStudentDocumentDownloadUrl,
  listSecretariatRegistry,
  createSecretariatRegistry,
  updateSecretariatRegistryStatus,
  listSecretariatRegistryAttachments,
  uploadSecretariatRegistryAttachment,
  deleteSecretariatRegistryAttachment,
  getSecretariatRegistryAttachmentDownloadUrl,
  listSecretariatReportYears,
  getSecretariatReportStudentsCsvUrl,
  getSecretariatReportAbsencesCsvUrl,
  getSecretariatReportGradesCsvUrl,
  renameSecretariatClass,
  resendSecretariatTeacherInvite,
  updateSecretariatGoogleSheet,
  type SecretariatArchivedCatalogRow,
  type SchoolListRow,
  type SecretariatStudentDossierRow,
  type SecretariatStudentDocumentRow,
  type SecretariatGoogleSheetLinkRow,
  type SecretariatRegistryRow,
  type SecretariatRegistryAttachmentRow,
  type SecretariatSyncedClassRow,
  type SecretariatTeacherRow,
  type SecretariatTransferFileRow,
  type SecretariatTransferRow,
} from '../../lib/secretariatApi';

type SecretariatSummary = {
  active_classes: number;
  archived_classes: number;
  pending_enroll_requests: number;
  teachers: number;
  archived_catalogs: number;
  recent_archived_catalogs: SecretariatArchivedCatalogRow[];
};

export default function SecretariatDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { me, uiRole, refresh, signOut } = useAuth();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const [showPwdModal, setShowPwdModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdError, setPwdError] = useState('');

  const [className, setClassName] = useState('');
  const [classAdminTeacherId, setClassAdminTeacherId] = useState<string>('');
  const [classesLoading, setClassesLoading] = useState(false);
  const [classesError, setClassesError] = useState('');
  const [classesView, setClassesView] = useState<'active' | 'archived'>('active');
  const [classes, setClasses] = useState<SecretariatSyncedClassRow[]>([]);
  const [openClassMenuId, setOpenClassMenuId] = useState<number | null>(null);
  const [classMenuPos, setClassMenuPos] = useState<{ top: number; left: number } | null>(null);

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<{ classroom_id: number; name: string } | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [archiveOnRename, setArchiveOnRename] = useState(true);
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameError, setRenameError] = useState('');

  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<{ classroom_id: number; name: string } | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState('');

  const [archiveCatalogs, setArchiveCatalogs] = useState<SecretariatArchivedCatalogRow[]>([]);
  const [archiveCatalogsLoading, setArchiveCatalogsLoading] = useState(false);
  const [archiveCatalogsError, setArchiveCatalogsError] = useState('');
  const [archiveCatalogsQuery, setArchiveCatalogsQuery] = useState('');
  const [archiveCatalogsYear, setArchiveCatalogsYear] = useState<string | null>(null);

  const [summary, setSummary] = useState<SecretariatSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');

  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState('');
  const [requests, setRequests] = useState<
    Array<{
      id: number;
      classroom_id: number;
      classroom_name: string;
      requester: { id: number; full_name?: string | null; email?: string | null; role?: string | null };
      status: string;
    }>
  >([]);

  const [teachers, setTeachers] = useState<SecretariatTeacherRow[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [teachersError, setTeachersError] = useState('');
  const [showAddTeacherModal, setShowAddTeacherModal] = useState(false);
  const [teacherForm, setTeacherForm] = useState({ full_name: '', email: '', phone: '' });
  const [teacherBusy, setTeacherBusy] = useState(false);

  const [selectedClassroomId, setSelectedClassroomId] = useState<number | null>(null);
  const [students, setStudents] = useState<StudentSummary[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState('');
  const [studentGraduateLoading, setStudentGraduateLoading] = useState<Record<number, boolean>>({});
  const [classGraduateBusy, setClassGraduateBusy] = useState(false);

  const [dossierQuery, setDossierQuery] = useState('');
  const [dossierClassroomId, setDossierClassroomId] = useState<number | null>(null);
  const [dossierStudents, setDossierStudents] = useState<SecretariatStudentDossierRow[]>([]);
  const [dossierLoading, setDossierLoading] = useState(false);
  const [dossierError, setDossierError] = useState('');
  const [dossierSelectedStudentId, setDossierSelectedStudentId] = useState<number | null>(null);

  const [archiveGraduatesQuery, setArchiveGraduatesQuery] = useState('');
  const [archiveGraduatesClassroomId, setArchiveGraduatesClassroomId] = useState<number | null>(null);
  const [archiveGraduates, setArchiveGraduates] = useState<SecretariatStudentDossierRow[]>([]);
  const [archiveGraduatesLoading, setArchiveGraduatesLoading] = useState(false);
  const [archiveGraduatesError, setArchiveGraduatesError] = useState('');

  const [transferSchools, setTransferSchools] = useState<SchoolListRow[]>([]);
  const [transferSchoolsLoading, setTransferSchoolsLoading] = useState(false);
  const [transferSchoolsError, setTransferSchoolsError] = useState('');

  const [transferOutgoing, setTransferOutgoing] = useState<SecretariatTransferRow[]>([]);
  const [transferIncoming, setTransferIncoming] = useState<SecretariatTransferRow[]>([]);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState('');

  const [transferClasses, setTransferClasses] = useState<SecretariatSyncedClassRow[]>([]);
  const [transferClassesLoading, setTransferClassesLoading] = useState(false);

  const [transferStudentQuery, setTransferStudentQuery] = useState('');
  const [transferStudentResults, setTransferStudentResults] = useState<SecretariatStudentDossierRow[]>([]);
  const [transferStudentResultsLoading, setTransferStudentResultsLoading] = useState(false);
  const [transferStudentId, setTransferStudentId] = useState<number | null>(null);
  const [transferToSchoolId, setTransferToSchoolId] = useState<number | null>(null);
  const [transferMessage, setTransferMessage] = useState('');
  const [transferCreateBusy, setTransferCreateBusy] = useState(false);
  const [transferCreateError, setTransferCreateError] = useState('');

  const [transferProcessOpen, setTransferProcessOpen] = useState(false);
  const [transferProcessTarget, setTransferProcessTarget] = useState<SecretariatTransferRow | null>(null);
  const [transferAssignClassId, setTransferAssignClassId] = useState<number | null>(null);
  const [transferProcessMessage, setTransferProcessMessage] = useState('');
  const [transferProcessBusy, setTransferProcessBusy] = useState(false);
  const [transferProcessError, setTransferProcessError] = useState('');

  const [transferFilesOpen, setTransferFilesOpen] = useState(false);
  const [transferFilesTarget, setTransferFilesTarget] = useState<SecretariatTransferRow | null>(null);
  const [transferFiles, setTransferFiles] = useState<SecretariatTransferFileRow[]>([]);
  const [transferFilesLoading, setTransferFilesLoading] = useState(false);
  const [transferFilesError, setTransferFilesError] = useState('');

  const [transferOutgoingPage, setTransferOutgoingPage] = useState(1);
  const [transferIncomingPage, setTransferIncomingPage] = useState(1);

  const [dossierDocs, setDossierDocs] = useState<SecretariatStudentDocumentRow[]>([]);
  const [dossierDocsLoading, setDossierDocsLoading] = useState(false);
  const [dossierDocsError, setDossierDocsError] = useState('');
  const [dossierUploadBusy, setDossierUploadBusy] = useState(false);
  const [dossierUploadError, setDossierUploadError] = useState('');
  const [dossierDocType, setDossierDocType] = useState<string>('dosar_inscriere');
  const [dossierDocTitle, setDossierDocTitle] = useState<string>('');

  const [registryRows, setRegistryRows] = useState<SecretariatRegistryRow[]>([]);
  const [registryLoading, setRegistryLoading] = useState(false);
  const [registryError, setRegistryError] = useState('');
  const [registryQuery, setRegistryQuery] = useState('');
  const [registryStatus, setRegistryStatus] = useState<string | null>(null);
  const [registryCategory, setRegistryCategory] = useState<string | null>(null);
  const [registryDirection, setRegistryDirection] = useState<string | null>(null);
  const [registryOpenId, setRegistryOpenId] = useState<number | null>(null);
  const [registryAttByEntry, setRegistryAttByEntry] = useState<Record<number, SecretariatRegistryAttachmentRow[]>>({});
  const [registryAttLoadingId, setRegistryAttLoadingId] = useState<number | null>(null);
  const [registryAttError, setRegistryAttError] = useState<string>('');
  const [registryAttUploadBusy, setRegistryAttUploadBusy] = useState(false);
  const [registryAttUploadTitle, setRegistryAttUploadTitle] = useState('');

  const [registryForm, setRegistryForm] = useState({
    category: 'cerere',
    direction: 'intrare',
    request_type: 'cerere_generala',
    subject: '',
    description: '',
    doc_number: '',
    issuer: '',
    recipient: '',
    requester_name: '',
    requester_email: '',
    requester_phone: '',
    student_id: '' as string | number,
  });
  const [registryBusy, setRegistryBusy] = useState(false);
  const [registryLastUpdatedAt, setRegistryLastUpdatedAt] = useState<string | null>(null);
  const registryLoadInFlightRef = useRef(false);

  const [reportsYears, setReportsYears] = useState<string[]>([]);
  const [reportsYearsLoading, setReportsYearsLoading] = useState(false);
  const [reportsYearsError, setReportsYearsError] = useState('');
  const [reportsSchoolYear, setReportsSchoolYear] = useState<string>('');
  const [reportsClassroomId, setReportsClassroomId] = useState<number | null>(null);
  const [reportsStudentsQuery, setReportsStudentsQuery] = useState<string>('');
  const [reportsDownloadBusy, setReportsDownloadBusy] = useState(false);
  const [reportsDownloadError, setReportsDownloadError] = useState('');

  const [sheetLinks, setSheetLinks] = useState<SecretariatGoogleSheetLinkRow[]>([]);
  const [sheetLinksLoading, setSheetLinksLoading] = useState(false);
  const [sheetLinksError, setSheetLinksError] = useState('');
  const [sheetTitle, setSheetTitle] = useState('');
  const [sheetUrl, setSheetUrl] = useState('');
  const [sheetCreateBusy, setSheetCreateBusy] = useState(false);
  const [sheetCreateError, setSheetCreateError] = useState('');
  const [sheetEditOpen, setSheetEditOpen] = useState(false);
  const [sheetEditTarget, setSheetEditTarget] = useState<SecretariatGoogleSheetLinkRow | null>(null);
  const [sheetEditTitle, setSheetEditTitle] = useState('');
  const [sheetEditUrl, setSheetEditUrl] = useState('');
  const [sheetEditBusy, setSheetEditBusy] = useState(false);
  const [sheetEditError, setSheetEditError] = useState('');
  const [sheetDeleteBusyId, setSheetDeleteBusyId] = useState<number | null>(null);

  const [detailStudents, setDetailStudents] = useState<StudentSummary[]>([]);
  const [detailStudentsLoading, setDetailStudentsLoading] = useState(false);
  const [detailStudentsError, setDetailStudentsError] = useState('');

  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [studentForm, setStudentForm] = useState({
    name: '',
    birthDate: '',
    manualOver16: true,
    studentEmail: '',
    studentPhone: '',
    studentAddress: '',
    parentName1: '',
    parentEmail1: '',
    parentPhone1: '',
    parentName2: '',
    parentEmail2: '',
    parentPhone2: '',
  });

  type TabKey =
    | 'overview'
    | 'classes'
    | 'archive'
    | 'requests'
    | 'users'
    | 'students'
    | 'transfers'
    | 'sheets'
    | 'documents'
    | 'registry'
    | 'reports'
    | 'settings';

  const tab = useMemo<TabKey>(() => {
    const params = new URLSearchParams(location.search);
    const t = (params.get('tab') || 'overview') as TabKey;
    return (
      [
        'overview',
        'classes',
        'archive',
        'requests',
        'users',
        'students',
        'transfers',
        'sheets',
        'documents',
        'registry',
        'reports',
        'settings',
      ] as TabKey[]
    ).includes(t)
      ? t
      : 'overview';
  }, [location.search]);

  type ArchiveTabKey = 'catalogs' | 'graduates';
  const archiveTab = useMemo<ArchiveTabKey>(() => {
    if (tab !== 'archive') return 'catalogs';
    const params = new URLSearchParams(location.search);
    const t = (params.get('archiveTab') || 'catalogs').trim() as ArchiveTabKey;
    return (['catalogs', 'graduates'] as ArchiveTabKey[]).includes(t) ? t : 'catalogs';
  }, [location.search, tab]);

  const titleByTab: Record<TabKey, string> = {
    overview: 'Panou secretariat',
    classes: 'Clase',
    archive: 'Arhivă',
    requests: 'Cereri aderare',
    users: 'Utilizatori',
    students: 'Elevi & dosare',
    transfers: 'Transferuri',
    sheets: 'Foi Google',
    documents: 'Documente',
    registry: 'Registratură',
    reports: 'Rapoarte',
    settings: 'Setări',
  };

  const goToArchiveGraduates = useCallback(async () => {
    const params = new URLSearchParams(location.search || '');
    params.set('tab', 'archive');
    params.set('archiveTab', 'graduates');
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' });
  }, [location.pathname, location.search, navigate]);

  type ClassDetailTab = 'catalog' | 'students' | 'teachers' | 'documents';
  const classDetailId = useMemo<number | null>(() => {
    if (tab !== 'classes') return null;
    const params = new URLSearchParams(location.search);
    const raw = (params.get('classId') || '').trim();
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [location.search, tab]);

  const classDetailTab = useMemo<ClassDetailTab>(() => {
    if (tab !== 'classes') return 'catalog';
    const params = new URLSearchParams(location.search);
    const raw = (params.get('classTab') || 'catalog').trim() as ClassDetailTab;
    return (['catalog', 'students', 'teachers', 'documents'] as ClassDetailTab[]).includes(raw) ? raw : 'catalog';
  }, [location.search, tab]);

  const classDetailCatalogYear = useMemo<string | null>(() => {
    if (tab !== 'classes') return null;
    const params = new URLSearchParams(location.search);
    const raw = (params.get('catalogYear') || '').trim();
    return raw ? raw : null;
  }, [location.search, tab]);

  const classDetail = useMemo(() => {
    if (!classDetailId) return null;
    return classes.find((c) => Number(c.classroom_id) === Number(classDetailId)) || null;
  }, [classes, classDetailId]);

  const [detailAdminTeacherId, setDetailAdminTeacherId] = useState<string>('');
  const [detailAdminBusy, setDetailAdminBusy] = useState(false);
  const [detailAdminError, setDetailAdminError] = useState('');
  const [detailAdminInfo, setDetailAdminInfo] = useState('');
  const [teachersPanelNonce, setTeachersPanelNonce] = useState(0);

  if (!me || uiRole !== 'secretariat') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-sm text-muted-foreground">Nu ești autentificat ca secretariat.</div>
      </div>
    );
  }

  useEffect(() => {
    if (me?.must_change_password) setShowPwdModal(true);
  }, [me?.must_change_password]);

  useEffect(() => {
    const onDocMouseDown = () => {
      setOpenClassMenuId(null);
      setClassMenuPos(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!openClassMenuId) return;
      setOpenClassMenuId(null);
      setClassMenuPos(null);
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [openClassMenuId]);

  const loadClasses = useCallback(async () => {
    setClassesLoading(true);
    setClassesError('');
    try {
      const rows = await listSecretariatClasses({ archived: classesView === 'archived' });
      setClasses(rows);
    } catch (e: any) {
      setClasses([]);
      setClassesError(e?.response?.data?.detail || 'Nu am putut încărca lista de clase.');
    } finally {
      setClassesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadClasses();
  }, [loadClasses]);

  const classAdminTeacherOptions = useMemo(() => {
    const opts = (teachers || []).map((t) => {
      const name = (t.full_name || '').toString().trim() || `Profesor #${String(t.id)}`;
      const email = (t.email || '').toString().trim();
      return { value: String(t.id), label: email ? `${name} • ${email}` : name };
    });
    opts.sort((a, b) => a.label.localeCompare(b.label, 'ro-RO'));
    return [{ value: '', label: 'Selectează dirigintele (admin)' }, ...opts];
  }, [teachers]);

  const canCreateClass = useMemo(() => {
    if (!className.trim()) return false;
    if (teachersLoading) return false;
    if ((teachers || []).length > 0 && !classAdminTeacherId) return false;
    return true;
  }, [classAdminTeacherId, className, teachers, teachersLoading]);

  const createClass = useCallback(async () => {
    const name = className.trim();
    if (!name) return;
    setClassesError('');
    try {
      const admin_professor_id = classAdminTeacherId ? Number(classAdminTeacherId) : undefined;
      await api.post('/api/secretariat/classes', { name, admin_professor_id });
      setClassName('');
      setClassAdminTeacherId('');
      await loadClasses();
    } catch (e: any) {
      setClassesError(e?.response?.data?.detail || 'Nu am putut crea clasa.');
    }
  }, [classAdminTeacherId, className, loadClasses]);

  const setClassAdmin = useCallback(async () => {
    if (!classDetailId) return;
    const pid = detailAdminTeacherId ? Number(detailAdminTeacherId) : NaN;
    if (!Number.isFinite(pid) || pid <= 0) {
      setDetailAdminError('Selectează un profesor diriginte.');
      return;
    }
    setDetailAdminBusy(true);
    setDetailAdminError('');
    setDetailAdminInfo('');
    try {
      await api.put(`/api/secretariat/classes/${classDetailId}/admin`, { professor_id: pid });
      setDetailAdminInfo('Dirigintele a fost setat.');
      setTeachersPanelNonce((n) => n + 1);
    } catch (e: any) {
      setDetailAdminError(e?.response?.data?.detail || 'Nu am putut seta dirigintele.');
    } finally {
      setDetailAdminBusy(false);
    }
  }, [classDetailId, detailAdminTeacherId]);

  const openClassDetail = useCallback(
    (classroomId: number, nextTab: 'catalog' | 'students' | 'teachers' | 'documents' = 'catalog') => {
      const params = new URLSearchParams(location.search);
      params.set('tab', 'classes');
      params.set('classId', String(classroomId));
      params.set('classTab', nextTab);
      navigate(`${location.pathname}?${params.toString()}`);
    },
    [location.pathname, location.search, navigate]
  );

  const closeClassDetail = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete('classId');
    params.delete('classTab');
    params.set('tab', 'classes');
    navigate(`${location.pathname}?${params.toString()}`);
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    setDetailAdminTeacherId('');
    setDetailAdminBusy(false);
    setDetailAdminError('');
    setDetailAdminInfo('');
  }, [classDetailId]);

  const setClassDetailTab = useCallback(
    (nextTab: 'catalog' | 'students' | 'teachers' | 'documents') => {
      if (!classDetailId) return;
      openClassDetail(classDetailId, nextTab);
    },
    [classDetailId, openClassDetail]
  );

  const loadDetailStudents = useCallback(async () => {
    if (!classDetailId) return;
    setDetailStudentsLoading(true);
    setDetailStudentsError('');
    try {
      const data = await fetchStudentsInClassroom(classDetailId);
      setDetailStudents(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setDetailStudents([]);
      setDetailStudentsError(e?.response?.data?.detail || 'Nu am putut încărca elevii.');
    } finally {
      setDetailStudentsLoading(false);
    }
  }, [classDetailId]);

  useEffect(() => {
    if (tab !== 'classes') return;
    if (!classDetailId) return;
    if (classDetailTab !== 'students') return;
    void loadDetailStudents();
  }, [classDetailId, classDetailTab, loadDetailStudents, tab]);

  const openRenameClass = useCallback((c: { classroom_id: number; name: string }) => {
    setRenameTarget({ classroom_id: Number(c.classroom_id), name: String(c.name || '').trim() });
    setRenameValue(String(c.name || '').trim());
    setArchiveOnRename(true);
    setRenameError('');
    setRenameOpen(true);
  }, []);

  const submitRenameClass = useCallback(async () => {
    if (!renameTarget) return;
    const next = renameValue.trim();
    if (!next) {
      setRenameError('Numele este obligatoriu.');
      return;
    }
    setRenameBusy(true);
    setRenameError('');
    try {
      await renameSecretariatClass(renameTarget.classroom_id, next, { archive_existing_data: archiveOnRename });
      setRenameOpen(false);
      setRenameTarget(null);
      await loadClasses();
    } catch (e: any) {
      setRenameError(e?.response?.data?.detail || 'Nu am putut redenumi clasa.');
    } finally {
      setRenameBusy(false);
    }
  }, [archiveOnRename, loadClasses, renameTarget, renameValue]);

  const openArchiveClass = useCallback((c: { classroom_id: number; name: string }) => {
    setArchiveTarget({ classroom_id: Number(c.classroom_id), name: String(c.name || '').trim() });
    setArchiveError('');
    setArchiveOpen(true);
  }, []);

  const submitArchiveClass = useCallback(async () => {
    if (!archiveTarget) return;
    setArchiveBusy(true);
    setArchiveError('');
    try {
      await archiveSecretariatClass(archiveTarget.classroom_id);
      setArchiveOpen(false);
      setArchiveTarget(null);
      if (classesView !== 'archived') setClassesView('archived');
      else await loadClasses();
    } catch (e: any) {
      setArchiveError(e?.response?.data?.detail || 'Nu am putut arhiva clasa.');
    } finally {
      setArchiveBusy(false);
    }
  }, [archiveTarget, classesView, loadClasses]);

  const loadArchiveCatalogs = useCallback(async () => {
    setArchiveCatalogsLoading(true);
    setArchiveCatalogsError('');
    try {
      const rows = await listSecretariatArchivedCatalogs({
        q: archiveCatalogsQuery.trim() || undefined,
        school_year: archiveCatalogsYear,
      });
      setArchiveCatalogs(rows);
    } catch (e: any) {
      setArchiveCatalogs([]);
      setArchiveCatalogsError(e?.response?.data?.detail || 'Nu am putut încărca arhiva.');
    } finally {
      setArchiveCatalogsLoading(false);
    }
  }, [archiveCatalogsQuery, archiveCatalogsYear]);

  const loadArchiveGraduates = useCallback(async () => {
    setArchiveGraduatesLoading(true);
    setArchiveGraduatesError('');
    try {
      const rows = await listSecretariatDossierStudents({
        q: archiveGraduatesQuery.trim() || undefined,
        classroom_id: archiveGraduatesClassroomId,
        include_graduated: true,
        limit: 200,
      });
      const onlyGraduated = (rows || []).filter((s) => !!(s as any)?.is_graduated);
      onlyGraduated.sort((a: any, b: any) => String(b?.graduated_at || '').localeCompare(String(a?.graduated_at || '')));
      setArchiveGraduates(onlyGraduated);
    } catch (e: any) {
      setArchiveGraduates([]);
      setArchiveGraduatesError(e?.response?.data?.detail || 'Nu am putut încărca elevii absolviți.');
    } finally {
      setArchiveGraduatesLoading(false);
    }
  }, [archiveGraduatesClassroomId, archiveGraduatesQuery]);

  useEffect(() => {
    if (tab !== 'archive') return;
    void loadArchiveCatalogs();
    void loadArchiveGraduates();
  }, [loadArchiveCatalogs, loadArchiveGraduates, tab]);

  const loadTransferSchools = useCallback(async () => {
    setTransferSchoolsLoading(true);
    setTransferSchoolsError('');
    try {
      const rows = await listAllSchools();
      const only = (rows || []).filter((s) => Number(s?.id) > 0 && Number(s?.id) !== Number(me.school_id));
      only.sort((a, b) => (a.county || '').localeCompare(b.county || '', 'ro-RO') || (a.city || '').localeCompare(b.city || '', 'ro-RO') || (a.name || '').localeCompare(b.name || '', 'ro-RO'));
      setTransferSchools(only);
    } catch (e: any) {
      setTransferSchools([]);
      setTransferSchoolsError(e?.response?.data?.detail || 'Nu am putut încărca lista de școli.');
    } finally {
      setTransferSchoolsLoading(false);
    }
  }, [me.school_id]);

  const loadTransferClasses = useCallback(async () => {
    setTransferClassesLoading(true);
    try {
      const rows = await listSecretariatClasses({ archived: false });
      setTransferClasses(rows || []);
    } catch {
      setTransferClasses([]);
    } finally {
      setTransferClassesLoading(false);
    }
  }, []);

  const loadTransfers = useCallback(async () => {
    setTransferLoading(true);
    setTransferError('');
    try {
      const [outgoing, incoming] = await Promise.all([listSecretariatOutgoingTransfers({ limit: 200 }), listSecretariatIncomingTransfers({ limit: 200 })]);
      setTransferOutgoing(Array.isArray(outgoing) ? outgoing : []);
      setTransferIncoming(Array.isArray(incoming) ? incoming : []);
      setTransferOutgoingPage(1);
      setTransferIncomingPage(1);
      void loadTransferSchools();
      void loadTransferClasses();
    } catch (e: any) {
      setTransferOutgoing([]);
      setTransferIncoming([]);
      setTransferError(e?.response?.data?.detail || 'Nu am putut încărca transferurile.');
    } finally {
      setTransferLoading(false);
    }
  }, [loadTransferClasses, loadTransferSchools]);

  useEffect(() => {
    if (tab !== 'transfers') return;
    void loadTransfers();
  }, [loadTransfers, tab]);

  useEffect(() => {
    if (tab !== 'transfers') return;
    const q = transferStudentQuery.trim();
    setTransferCreateError('');
    if (!q) {
      setTransferStudentResults([]);
      return;
    }
    let canceled = false;
    setTransferStudentResultsLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const rows = await listSecretariatDossierStudents({ q, limit: 10 });
        if (canceled) return;
        const only = (rows || []).filter((s) => !(s as any)?.is_graduated);
        setTransferStudentResults(only);
      } catch {
        if (canceled) return;
        setTransferStudentResults([]);
      } finally {
        if (!canceled) setTransferStudentResultsLoading(false);
      }
    }, 250);
    return () => {
      canceled = true;
      window.clearTimeout(t);
    };
  }, [tab, transferStudentQuery]);

  const submitCreateTransfer = useCallback(async () => {
    if (!transferStudentId) {
      setTransferCreateError('Selectează un elev.');
      return;
    }
    if (!transferToSchoolId) {
      setTransferCreateError('Selectează școala de destinație.');
      return;
    }
    setTransferCreateBusy(true);
    setTransferCreateError('');
    try {
      await createSecretariatTransfer({ student_id: transferStudentId, to_school_id: transferToSchoolId, message: transferMessage.trim() || null });
      setTransferStudentQuery('');
      setTransferStudentResults([]);
      setTransferStudentId(null);
      setTransferToSchoolId(null);
      setTransferMessage('');
      await loadTransfers();
    } catch (e: any) {
      setTransferCreateError(e?.response?.data?.detail || 'Nu am putut iniția transferul.');
    } finally {
      setTransferCreateBusy(false);
    }
  }, [loadTransfers, transferMessage, transferStudentId, transferToSchoolId]);

  const openTransferAccept = useCallback((t: SecretariatTransferRow) => {
    setTransferProcessTarget(t);
    setTransferAssignClassId(null);
    setTransferProcessMessage('');
    setTransferProcessError('');
    setTransferProcessOpen(true);
  }, []);

  const submitTransferAccept = useCallback(async () => {
    if (!transferProcessTarget) return;
    setTransferProcessBusy(true);
    setTransferProcessError('');
    try {
      await acceptSecretariatTransfer(transferProcessTarget.id, { assigned_school_class_id: transferAssignClassId, message: transferProcessMessage.trim() || null });
      setTransferProcessOpen(false);
      setTransferProcessTarget(null);
      await loadTransfers();
    } catch (e: any) {
      setTransferProcessError(e?.response?.data?.detail || 'Nu am putut accepta transferul.');
    } finally {
      setTransferProcessBusy(false);
    }
  }, [acceptSecretariatTransfer, loadTransfers, transferAssignClassId, transferProcessMessage, transferProcessTarget]);

  const submitTransferReject = useCallback(async (t: SecretariatTransferRow) => {
    setTransferProcessBusy(true);
    setTransferProcessError('');
    try {
      await rejectSecretariatTransfer(t.id, { message: (transferProcessMessage || '').trim() || null });
      setTransferProcessOpen(false);
      setTransferProcessTarget(null);
      await loadTransfers();
    } catch (e: any) {
      setTransferProcessError(e?.response?.data?.detail || 'Nu am putut respinge transferul.');
    } finally {
      setTransferProcessBusy(false);
    }
  }, [loadTransfers, rejectSecretariatTransfer, transferProcessMessage]);

  const openTransferFiles = useCallback(async (t: SecretariatTransferRow) => {
    setTransferFilesTarget(t);
    setTransferFiles([]);
    setTransferFilesError('');
    setTransferFilesOpen(true);
    setTransferFilesLoading(true);
    try {
      const rows = await listSecretariatTransferFiles(t.id);
      setTransferFiles(Array.isArray(rows) ? rows : []);
    } catch (e: any) {
      setTransferFiles([]);
      setTransferFilesError(e?.response?.data?.detail || 'Nu am putut încărca fișierele.');
    } finally {
      setTransferFilesLoading(false);
    }
  }, [listSecretariatTransferFiles]);

  const transferSchoolOptions = useMemo(() => {
    const opts = (transferSchools || []).map((s) => {
      const name = String(s.name || '').trim() || `Școala #${String(s.id)}`;
      const meta = [String(s.city || '').trim(), String(s.county || '').trim()].filter(Boolean).join(', ');
      return { value: String(s.id), label: meta ? `${name} • ${meta}` : name };
    });
    opts.sort((a, b) => a.label.localeCompare(b.label, 'ro-RO'));
    return [{ value: '', label: 'Selectează școala de destinație' }, ...opts];
  }, [transferSchools]);

  const transferClassOptions = useMemo(() => {
    const opts = (transferClasses || []).map((c) => ({ value: String(c.school_class_id), label: String(c.name || '').trim() || `Clasa #${String(c.school_class_id)}` }));
    opts.sort((a, b) => a.label.localeCompare(b.label, 'ro-RO'));
    return [{ value: '', label: 'Selectează clasa (opțional)' }, ...opts];
  }, [transferClasses]);

  const TRANSFER_PAGE_SIZE = 10;
  const transferOutgoingTotalPages = useMemo(() => Math.max(1, Math.ceil((transferOutgoing || []).length / TRANSFER_PAGE_SIZE)), [transferOutgoing]);
  const transferIncomingTotalPages = useMemo(() => Math.max(1, Math.ceil((transferIncoming || []).length / TRANSFER_PAGE_SIZE)), [transferIncoming]);

  const transferOutgoingPageRows = useMemo(() => {
    const start = (Math.max(1, transferOutgoingPage) - 1) * TRANSFER_PAGE_SIZE;
    return (transferOutgoing || []).slice(start, start + TRANSFER_PAGE_SIZE);
  }, [transferOutgoing, transferOutgoingPage]);

  const transferIncomingPageRows = useMemo(() => {
    const start = (Math.max(1, transferIncomingPage) - 1) * TRANSFER_PAGE_SIZE;
    return (transferIncoming || []).slice(start, start + TRANSFER_PAGE_SIZE);
  }, [transferIncoming, transferIncomingPage]);

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    setRequestsError('');
    try {
      const resp = await api.get('/api/secretariat/enroll-requests', { params: { status: 'pending' } });
      setRequests(Array.isArray(resp.data) ? (resp.data as any) : []);
    } catch (e: any) {
      setRequests([]);
      setRequestsError(e?.response?.data?.detail || 'Nu am putut încărca cererile.');
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  const acceptRequest = useCallback(
    async (requestId: number) => {
      try {
        await api.post(`/api/secretariat/enroll-requests/${requestId}/accept`);
        await loadRequests();
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut accepta cererea.');
      }
    },
    [loadRequests]
  );

  const rejectRequest = useCallback(
    async (requestId: number) => {
      try {
        await api.post(`/api/secretariat/enroll-requests/${requestId}/reject`);
        await loadRequests();
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut respinge cererea.');
      }
    },
    [loadRequests]
  );

  const isOver16 = useMemo(() => {
    const birthDate = (studentForm.birthDate || '').trim();
    if (!birthDate) return Boolean(studentForm.manualOver16);
    const d = new Date(birthDate);
    if (Number.isNaN(d.getTime())) return Boolean(studentForm.manualOver16);
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
    return age >= 16;
  }, [studentForm.birthDate, studentForm.manualOver16]);

  const classroomOptions = useMemo(() => {
    return (classes || []).map((c) => ({ value: c.classroom_id, label: c.name }));
  }, [classes]);

  const archiveYearOptions = useMemo(() => {
    const years = Array.from(new Set((archiveCatalogs || []).map((r) => String(r.school_year || '').trim()).filter(Boolean)));
    years.sort((a, b) => b.localeCompare(a));
    return [{ value: '', label: 'Toți anii' }, ...years.map((y) => ({ value: y, label: y }))];
  }, [archiveCatalogs]);

  const dossierClassOptions = useMemo(() => {
    return [{ value: '', label: 'Toate clasele' }, ...(classes || []).map((c) => ({ value: c.classroom_id, label: c.name }))];
  }, [classes]);

  const dossierSelectedStudent = useMemo(() => {
    if (!dossierSelectedStudentId) return null;
    return dossierStudents.find((s) => Number(s.id) === Number(dossierSelectedStudentId)) || null;
  }, [dossierSelectedStudentId, dossierStudents]);

  const loadDossierStudents = useCallback(async () => {
    setDossierLoading(true);
    setDossierError('');
    try {
      const rows = await listSecretariatDossierStudents({
        q: dossierQuery.trim() || undefined,
        classroom_id: dossierClassroomId,
        limit: 100,
      });
      setDossierStudents(rows);
    } catch (e: any) {
      setDossierStudents([]);
      setDossierError(e?.response?.data?.detail || 'Nu am putut încărca lista de elevi.');
    } finally {
      setDossierLoading(false);
    }
  }, [dossierClassroomId, dossierQuery]);

  const loadDossierDocs = useCallback(async (studentId: number) => {
    setDossierDocsLoading(true);
    setDossierDocsError('');
    try {
      const rows = await listSecretariatStudentDocuments(studentId);
      setDossierDocs(rows);
    } catch (e: any) {
      setDossierDocs([]);
      setDossierDocsError(e?.response?.data?.detail || 'Nu am putut încărca documentele elevului.');
    } finally {
      setDossierDocsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedClassroomId) return;
    if (!classes || classes.length === 0) return;
    setSelectedClassroomId(Number(classes[0].classroom_id));
  }, [classes, selectedClassroomId]);

  const loadTeachers = useCallback(async () => {
    setTeachersLoading(true);
    setTeachersError('');
    try {
      const list = await listSecretariatTeachers();
      setTeachers(list);
    } catch (e: any) {
      setTeachers([]);
      setTeachersError(e?.response?.data?.detail || 'Nu am putut încărca lista de profesori.');
    } finally {
      setTeachersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab !== 'users' && tab !== 'classes') return;
    void loadTeachers();
  }, [loadTeachers, tab]);

  const loadSheets = useCallback(async () => {
    setSheetLinksLoading(true);
    setSheetLinksError('');
    try {
      const rows = await listSecretariatGoogleSheets();
      setSheetLinks(rows);
    } catch (e: any) {
      setSheetLinks([]);
      setSheetLinksError(e?.response?.data?.detail || 'Nu am putut încărca lista de foi Google.');
    } finally {
      setSheetLinksLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab !== 'sheets') return;
    void loadSheets();
  }, [loadSheets, tab]);

  const submitCreateSheet = useCallback(async () => {
    const title = sheetTitle.trim();
    const url = sheetUrl.trim();
    if (!title) {
      setSheetCreateError('Titlul este obligatoriu.');
      return;
    }
    if (!url) {
      setSheetCreateError('URL este obligatoriu.');
      return;
    }

    setSheetCreateBusy(true);
    setSheetCreateError('');
    try {
      await createSecretariatGoogleSheet({ title, url });
      setSheetTitle('');
      setSheetUrl('');
      await loadSheets();
    } catch (e: any) {
      setSheetCreateError(e?.response?.data?.detail || 'Nu am putut adăuga foaia Google.');
    } finally {
      setSheetCreateBusy(false);
    }
  }, [createSecretariatGoogleSheet, loadSheets, sheetTitle, sheetUrl]);

  const submitUpdateSheet = useCallback(async () => {
    if (!sheetEditTarget) return;
    const title = sheetEditTitle.trim();
    const url = sheetEditUrl.trim();
    if (!title) {
      setSheetEditError('Titlul este obligatoriu.');
      return;
    }
    if (!url) {
      setSheetEditError('URL este obligatoriu.');
      return;
    }

    setSheetEditBusy(true);
    setSheetEditError('');
    try {
      await updateSecretariatGoogleSheet(sheetEditTarget.id, { title, url });
      setSheetEditOpen(false);
      setSheetEditTarget(null);
      await loadSheets();
    } catch (e: any) {
      setSheetEditError(e?.response?.data?.detail || 'Nu am putut salva modificările.');
    } finally {
      setSheetEditBusy(false);
    }
  }, [loadSheets, sheetEditTarget, sheetEditTitle, sheetEditUrl, updateSecretariatGoogleSheet]);

  const submitDeleteSheet = useCallback(
    async (id: number) => {
      if (!window.confirm('Sigur vrei să ștergi acest link?')) return;
      setSheetLinksError('');
      setSheetDeleteBusyId(id);
      try {
        await deleteSecretariatGoogleSheet(id);
        await loadSheets();
      } catch (e: any) {
        setSheetLinksError(e?.response?.data?.detail || 'Nu am putut șterge linkul.');
      } finally {
        setSheetDeleteBusyId(null);
      }
    },
    [deleteSecretariatGoogleSheet, loadSheets]
  );

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const resp = await api.get('/api/secretariat/dashboard/summary');
      setSummary((resp.data || null) as any);
    } catch (e: any) {
      setSummary(null);
      setSummaryError(e?.response?.data?.detail || 'Nu am putut încărca sumarul.');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab !== 'overview') return;
    void loadSummary();
  }, [loadSummary, tab]);

  useEffect(() => {
    if (tab !== 'students') return;
    void loadDossierStudents();
  }, [loadDossierStudents, tab]);

  useEffect(() => {
    if (tab !== 'students') return;
    if (dossierSelectedStudentId) return;
    if (!dossierStudents || dossierStudents.length === 0) return;
    setDossierSelectedStudentId(Number(dossierStudents[0].id));
  }, [dossierSelectedStudentId, dossierStudents, tab]);

  useEffect(() => {
    if (tab !== 'students') return;
    if (!dossierSelectedStudentId) return;
    void loadDossierDocs(dossierSelectedStudentId);
  }, [dossierSelectedStudentId, loadDossierDocs, tab]);

  const submitDossierUpload = useCallback(
    async (file: File) => {
      if (!dossierSelectedStudentId) return;
      setDossierUploadBusy(true);
      setDossierUploadError('');
      try {
        await uploadSecretariatStudentDocument(dossierSelectedStudentId, {
          file,
          doc_type: dossierDocType,
          title: dossierDocTitle.trim() || null,
        });
        setDossierDocTitle('');
        await loadDossierDocs(dossierSelectedStudentId);
      } catch (e: any) {
        setDossierUploadError(e?.response?.data?.detail || 'Nu am putut încărca documentul.');
      } finally {
        setDossierUploadBusy(false);
      }
    },
    [dossierDocTitle, dossierDocType, dossierSelectedStudentId, loadDossierDocs]
  );

  const deleteDossierDoc = useCallback(
    async (docId: number) => {
      if (!dossierSelectedStudentId) return;
      setDossierDocsError('');
      try {
        await deleteSecretariatStudentDocument(docId);
        await loadDossierDocs(dossierSelectedStudentId);
      } catch (e: any) {
        setDossierDocsError(e?.response?.data?.detail || 'Nu am putut șterge documentul.');
      }
    },
    [dossierSelectedStudentId, loadDossierDocs]
  );

  const registryStudentOptions = useMemo(() => {
    const base = [{ value: '', label: 'Fără elev asociat' }];
    const opts = (dossierStudents || []).slice(0, 200).map((s) => ({
      value: String(s.id),
      label: `${s.full_name}${s.classroom_name ? ` (${s.classroom_name})` : ''}`,
    }));
    return [...base, ...opts];
  }, [dossierStudents]);

  const loadRegistry = useCallback(
    async ({ silent }: { silent?: boolean } = {}) => {
      if (registryLoadInFlightRef.current) return;
      registryLoadInFlightRef.current = true;

      if (!silent) {
        setRegistryLoading(true);
        setRegistryError('');
      }
      try {
        const rows = await listSecretariatRegistry({
          q: registryQuery.trim() || undefined,
          category: registryCategory,
          direction: registryDirection,
          status: registryStatus,
          limit: 200,
        });
        setRegistryRows(rows);
        setRegistryError('');
        setRegistryLastUpdatedAt(new Date().toISOString());
      } catch (e: any) {
        if (!silent) {
          setRegistryRows([]);
          setRegistryError(e?.response?.data?.detail || 'Nu am putut încărca registratura.');
        }
      } finally {
        if (!silent) setRegistryLoading(false);
        registryLoadInFlightRef.current = false;
      }
    },
    [registryCategory, registryDirection, registryQuery, registryStatus]
  );

  useEffect(() => {
    if (tab !== 'registry') return;
    void loadRegistry();
    if (dossierStudents.length === 0) void loadDossierStudents();
  }, [dossierStudents.length, loadDossierStudents, loadRegistry, tab]);

  useEffect(() => {
    if (tab !== 'registry') return;

    const refreshIfVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void loadRegistry({ silent: true });
    };

    const pollId = window.setInterval(refreshIfVisible, 2500);
    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener('visibilitychange', refreshIfVisible);

    return () => {
      window.clearInterval(pollId);
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [loadRegistry, tab]);

  const loadReportYears = useCallback(async () => {
    setReportsYearsLoading(true);
    setReportsYearsError('');
    try {
      const years = await listSecretariatReportYears();
      setReportsYears(years);
      setReportsSchoolYear((prev) => (prev ? prev : years[0] || ''));
    } catch (e: any) {
      setReportsYears([]);
      setReportsYearsError(e?.response?.data?.detail || 'Nu am putut încărca anii școlari.');
    } finally {
      setReportsYearsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab !== 'reports') return;
    if (reportsYears.length === 0 && !reportsYearsLoading) void loadReportYears();
  }, [loadReportYears, reportsYears.length, reportsYearsLoading, tab]);

  const downloadReportCsv = useCallback(
    async (url: string, fallbackFilename: string) => {
      setReportsDownloadBusy(true);
      setReportsDownloadError('');
      try {
        const resp = await api.get(url, { responseType: 'blob' });
        const contentType = (resp.headers?.['content-type'] as string | undefined) || 'text/csv; charset=utf-8';
        const blob = new Blob([resp.data], { type: contentType });

        let filename = fallbackFilename;
        const cd = (resp.headers?.['content-disposition'] as string | undefined) || '';
        const m = cd.match(/filename=\"?([^\";]+)\"?/i);
        if (m && m[1]) filename = m[1];

        const href = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(href);
      } catch (e: any) {
        setReportsDownloadError(e?.response?.data?.detail || 'Nu am putut descărca raportul.');
      } finally {
        setReportsDownloadBusy(false);
      }
    },
    []
  );

  const submitRegistry = useCallback(async () => {
    const request_type = String(registryForm.request_type || '').trim();
    if (!request_type) {
      setRegistryError('Tipul cererii este obligatoriu.');
      return;
    }
    setRegistryBusy(true);
    setRegistryError('');
    try {
      const studentId = String(registryForm.student_id || '').trim();
      await createSecretariatRegistry({
        category: String(registryForm.category || 'cerere'),
        direction: String(registryForm.direction || 'intrare'),
        request_type,
        subject: registryForm.subject.trim() || null,
        description: registryForm.description.trim() || null,
        doc_number: registryForm.doc_number.trim() || null,
        issuer: registryForm.issuer.trim() || null,
        recipient: registryForm.recipient.trim() || null,
        requester_name: registryForm.requester_name.trim() || null,
        requester_email: registryForm.requester_email.trim() || null,
        requester_phone: registryForm.requester_phone.trim() || null,
        student_id: studentId ? Number(studentId) : null,
      });
      setRegistryForm((p) => ({ ...p, subject: '', description: '', doc_number: '', issuer: '', recipient: '' }));
      await loadRegistry();
    } catch (e: any) {
      setRegistryError(e?.response?.data?.detail || 'Nu am putut crea înregistrarea.');
    } finally {
      setRegistryBusy(false);
    }
  }, [loadRegistry, registryForm]);

  const setRegistryRowStatus = useCallback(
    async (entryId: number, nextStatus: string) => {
      setRegistryError('');
      try {
        await updateSecretariatRegistryStatus(entryId, nextStatus);
        await loadRegistry();
      } catch (e: any) {
        setRegistryError(e?.response?.data?.detail || 'Nu am putut actualiza statusul.');
      }
    },
    [loadRegistry]
  );

  const loadRegistryAttachments = useCallback(async (entryId: number) => {
    setRegistryAttLoadingId(entryId);
    setRegistryAttError('');
    try {
      const rows = await listSecretariatRegistryAttachments(entryId);
      setRegistryAttByEntry((p) => ({ ...p, [entryId]: rows }));
    } catch (e: any) {
      setRegistryAttByEntry((p) => ({ ...p, [entryId]: [] }));
      setRegistryAttError(e?.response?.data?.detail || 'Nu am putut încărca atașamentele.');
    } finally {
      setRegistryAttLoadingId(null);
    }
  }, []);

  const uploadRegistryAttachment = useCallback(
    async (entryId: number, file: File) => {
      setRegistryAttUploadBusy(true);
      setRegistryAttError('');
      try {
        await uploadSecretariatRegistryAttachment(entryId, { file, title: registryAttUploadTitle.trim() || null });
        setRegistryAttUploadTitle('');
        await loadRegistryAttachments(entryId);
      } catch (e: any) {
        setRegistryAttError(e?.response?.data?.detail || 'Nu am putut încărca fișierul.');
      } finally {
        setRegistryAttUploadBusy(false);
      }
    },
    [loadRegistryAttachments, registryAttUploadTitle]
  );

  const deleteRegistryAttachment = useCallback(
    async (entryId: number, attId: number) => {
      setRegistryAttError('');
      try {
        await deleteSecretariatRegistryAttachment(attId);
        await loadRegistryAttachments(entryId);
      } catch (e: any) {
        setRegistryAttError(e?.response?.data?.detail || 'Nu am putut șterge fișierul.');
      }
    },
    [loadRegistryAttachments]
  );

  const submitCreateTeacher = useCallback(async () => {
    const full_name = (teacherForm.full_name || '').trim();
    const email = (teacherForm.email || '').trim().toLowerCase();
    const phone = (teacherForm.phone || '').trim();
    if (!full_name || !email) {
      alert('Nume și email obligatorii.');
      return;
    }
    setTeacherBusy(true);
    try {
      await createSecretariatTeacher({ full_name, email, phone: phone || null });
      setTeacherForm({ full_name: '', email: '', phone: '' });
      setShowAddTeacherModal(false);
      await loadTeachers();
      alert('Profesor creat. Parola temporară a fost trimisă pe email și trebuie schimbată la primul login.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut crea profesorul.');
    } finally {
      setTeacherBusy(false);
    }
  }, [loadTeachers, teacherForm.email, teacherForm.full_name, teacherForm.phone]);

  const resendTeacherInvite = useCallback(async (teacherId: number) => {
    const ok = window.confirm('Retrimiți invitația (parolă nouă) pe email?');
    if (!ok) return;
    try {
      await resendSecretariatTeacherInvite(teacherId);
      alert('Invitația a fost retrimisă.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut retrimite invitația.');
    }
  }, []);

  const loadStudents = useCallback(async (classroomId: number) => {
    setStudentsLoading(true);
    setStudentsError('');
    try {
      const list = await fetchStudentsInClassroom(classroomId);
      setStudents(Array.isArray(list) ? list : []);
    } catch (e: any) {
      setStudents([]);
      setStudentsError(e?.response?.data?.detail || 'Nu am putut încărca elevii.');
    } finally {
      setStudentsLoading(false);
    }
  }, []);

  const resendStudentInvite = useCallback(
    async (studentId: number) => {
      if (!selectedClassroomId) return;
      const ok = window.confirm('Retrimiți linkul de confirmare pe email (elev 16+ și/sau părinți neconfirmați)?');
      if (!ok) return;
      try {
        const resp = await api.post(`/api/secretariat/classrooms/${selectedClassroomId}/students/${studentId}/resend-invite`, {});
        await loadStudents(selectedClassroomId);
        const d = resp?.data || {};
        const parts: string[] = [];
        if (d.student_sent) parts.push('elev');
        if (d.parent1_sent || d.parent2_sent) parts.push('părinți');
        alert(parts.length ? `Link retrimis: ${parts.join(' + ')}` : 'Nu a fost necesară retrimiterea (deja confirmat sau lipsă email).');
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut retrimite invitația.');
      }
    },
    [loadStudents, selectedClassroomId]
  );

  const exportUserJson = useCallback(async (userId: number) => {
    try {
      const resp = await api.get(`/api/admin/school/users/${userId}/export.json`, { responseType: 'blob' });
      const cd = String((resp.headers as any)?.['content-disposition'] || '');
      const m = cd.match(/filename=\"?([^\";]+)\"?/i);
      const fallbackName = `user_${userId}_export.json`;
      const filename = (m?.[1] || '').trim() || fallbackName;
      const blob = new Blob([resp.data], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut exporta datele utilizatorului.');
    }
  }, []);

  const editUserBasic = useCallback(async (opts: { id: number; full_name: string; email: string; phone?: string | null; address?: string | null; after?: () => void }) => {
    const full_name = window.prompt('Nume', String(opts.full_name || '')) ?? '';
    if (!full_name.trim()) return;
    const email = window.prompt('Email', String(opts.email || '')) ?? '';
    if (!email.trim()) return;
    const phone = window.prompt('Telefon (optional)', String(opts.phone || '')) ?? '';
    const address = window.prompt('Adresa (optional)', String(opts.address || '')) ?? '';
    try {
      await api.put(`/api/admin/school/users/${opts.id}`, { full_name, email, phone, address });
      if (opts.after) opts.after();
      alert('Modificari salvate.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut salva modificarile.');
    }
  }, []);

  const deleteUserAccount = useCallback(async (opts: { id: number; label: string; after?: () => void }) => {
    const ok = window.confirm(`Soft-delete pentru ${opts.label}? Contul va fi dezactivat si anonimizat, iar datele raman in platforma.`);
    if (!ok) return;
    try {
      await api.delete(`/api/admin/school/users/${opts.id}`);
      if (opts.after) opts.after();
      alert('Cont sters.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut sterge contul.');
    }
  }, []);

  const graduateUserAccount = useCallback(
    async (opts: { id: number; label: string; after?: () => void }) => {
      const ok = window.confirm(
        `Absolvire pentru ${opts.label}?\n\nContul va fi arhivat: login blocat, sesiuni/dispozitive revocate. Datele raman pentru audit.`
      );
      if (!ok) return;

      setStudentGraduateLoading((p) => ({ ...p, [opts.id]: true }));
      try {
        await api.post(`/api/admin/school/users/${opts.id}/graduate`, { reason: 'manual' });
        if (opts.after) opts.after();
        alert('Absolvire efectuata.');
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut efectua absolvirea.');
      } finally {
        setStudentGraduateLoading((p) => ({ ...p, [opts.id]: false }));
      }
    },
    []
  );

  const graduateSelectedClassroom = useCallback(async () => {
    if (!selectedClassroomId) return;
    const name = classroomOptions.find((o) => Number(o.value) === Number(selectedClassroomId))?.label || 'clasa selectata';
    const ok = window.confirm(
      `Absolvire pentru ${name}?\n\nToti elevii din clasa vor fi arhivati (login blocat, sesiuni/dispozitive revocate).`
    );
    if (!ok) return;

    setClassGraduateBusy(true);
    try {
      const resp = await api.post(`/api/admin/school/classrooms/${selectedClassroomId}/graduate`, { reason: 'bulk', archive_class: true });
      const count = Number(resp.data?.graduated_users || 0);
      alert(`Absolvire efectuata: ${count} elevi arhivati.`);
      await loadClasses();
      await loadStudents(selectedClassroomId);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut efectua absolvirea clasei.');
    } finally {
      setClassGraduateBusy(false);
    }
  }, [classroomOptions, loadClasses, loadStudents, selectedClassroomId]);

  useEffect(() => {
    if (tab !== 'users') return;
    if (!selectedClassroomId) return;
    void loadStudents(selectedClassroomId);
  }, [loadStudents, selectedClassroomId, tab]);

  const submitAddStudent = useCallback(async () => {
    if (!selectedClassroomId) return;
    setFormError('');
    const name = (studentForm.name || '').trim();
    if (!name) {
      setFormError('Completează numele elevului.');
      return;
    }
    if (isOver16 && !String(studentForm.studentEmail || '').trim()) {
      setFormError('Completează emailul elevului.');
      return;
    }
    if (!isOver16) {
      const mamaName = String(studentForm.parentName1 || '').trim();
      const mamaEmail = String(studentForm.parentEmail1 || '').trim();
      const tataName = String(studentForm.parentName2 || '').trim();
      const tataEmail = String(studentForm.parentEmail2 || '').trim();

      if (!mamaName && !tataName) {
        setFormError('Completează numele pentru cel puțin un părinte (mamă sau tată).');
        return;
      }
      if (!mamaEmail && !tataEmail) {
        setFormError('Completează emailul pentru cel puțin un părinte (mamă sau tată).');
        return;
      }
      if (mamaEmail && !mamaName) {
        setFormError('Completează și numele mamei dacă introduci emailul mamei.');
        return;
      }
      if (tataEmail && !tataName) {
        setFormError('Completează și numele tatălui dacă introduci emailul tatălui.');
        return;
      }
    }
    const payload: StudentCreatePayload = {
      name,
      birth_date: (studentForm.birthDate || '').trim() ? (studentForm.birthDate || '').trim() : null,
      over16: Boolean(isOver16),
      student_email: isOver16 ? (studentForm.studentEmail || '').trim() || null : null,
      student_phone: (studentForm.studentPhone || '').trim() || null,
      student_address: (studentForm.studentAddress || '').trim() || null,
      parent_name1: (studentForm.parentName1 || '').trim() || null,
      parent_email1: (studentForm.parentEmail1 || '').trim() || null,
      parent_phone1: (studentForm.parentPhone1 || '').trim() || null,
      parent_name2: (studentForm.parentName2 || '').trim() || null,
      parent_email2: (studentForm.parentEmail2 || '').trim() || null,
      parent_phone2: (studentForm.parentPhone2 || '').trim() || null,
    };

    setFormBusy(true);
    try {
      await addStudentToClassroomAsSecretariat(selectedClassroomId, payload);
      setShowAddStudentModal(false);
      setStudentForm({
        name: '',
        birthDate: '',
        manualOver16: true,
        studentEmail: '',
        studentPhone: '',
        studentAddress: '',
        parentName1: '',
        parentEmail1: '',
        parentPhone1: '',
        parentName2: '',
        parentEmail2: '',
        parentPhone2: '',
      });
      await loadStudents(selectedClassroomId);
      alert('Elev adăugat. Invitațiile pe email au fost trimise automat.');
    } catch (e: any) {
      setFormError(e?.response?.data?.detail || 'Nu am putut adăuga elevul.');
    } finally {
      setFormBusy(false);
    }
  }, [isOver16, loadStudents, selectedClassroomId, studentForm]);

  const validatePassword = (pwd: string) => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(pwd);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setPwdError('Parolele nu coincid.');
      return;
    }
    if (!validatePassword(newPassword)) {
      setPwdError('Parola trebuie sa contina litere mari/mici, cifre si simboluri.');
      return;
    }
    setPwdLoading(true);
    setPwdError('');
    try {
      await api.put('/api/users/me/password/initial', {
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      await refresh();
      alert('Parola a fost schimbata. Vei fi delogat.');
      signOut();
      navigate('/authentication-portal');
    } catch (err: any) {
      setPwdError(err?.response?.data?.detail || 'Eroare la schimbarea parolei.');
    } finally {
      setPwdLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Panou secretariat - Scola</title>
        <meta name="description" content="Panou secretariat (în lucru)." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header
          user={{ role: 'secretariat', name: me.full_name || 'Secretariat', avatar: me.avatar_url || undefined }}
          onNavigate={(p) => navigate(p)}
          onSignOut={() => {
            signOut();
            navigate('/authentication-portal');
          }}
        />

        <div className="min-h-screen bg-background pt-16">
          <DashboardSidebar user={{ role: 'secretariat' }} isCollapsed={isSidebarCollapsed} onNavigate={(p) => navigate(p)} />

          <main className={`transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
            <div className="p-6 max-w-6xl mx-auto">
              <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setIsSidebarCollapsed((p) => !p)}
                    className="hidden lg:flex items-center justify-center w-10 h-10 rounded-md hover:bg-muted transition-colors"
                    aria-label="Comută bara laterală"
                  >
                    <Icon name={isSidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} size={20} />
                  </button>
                  <div>
                    <h1 className="text-2xl font-bold text-foreground">{titleByTab[tab]}</h1>
                    <p className="text-sm text-muted-foreground">{me.full_name || 'Secretariat'}</p>
                  </div>
                </div>
              </div>

              {tab === 'overview' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="bg-card border border-border rounded-lg p-4">
                      <div className="text-xs text-muted-foreground">Clase active</div>
                      <div className="mt-1 text-2xl font-semibold text-foreground">
                        {summaryLoading ? '—' : String(summary?.active_classes ?? 0)}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3"
                        onClick={() => {
                          const params = new URLSearchParams(location.search);
                          params.set('tab', 'classes');
                          navigate(`${location.pathname}?${params.toString()}`);
                        }}
                      >
                        Vezi clase
                      </Button>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-4">
                      <div className="text-xs text-muted-foreground">Cataloage arhivate</div>
                      <div className="mt-1 text-2xl font-semibold text-foreground">
                        {summaryLoading ? '—' : String(summary?.archived_catalogs ?? 0)}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3"
                        onClick={() => {
                          const params = new URLSearchParams(location.search);
                          params.set('tab', 'archive');
                          navigate(`${location.pathname}?${params.toString()}`);
                        }}
                      >
                        Deschide arhiva
                      </Button>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-4">
                      <div className="text-xs text-muted-foreground">Cereri aderare</div>
                      <div className="mt-1 text-2xl font-semibold text-foreground">
                        {summaryLoading ? '—' : String(summary?.pending_enroll_requests ?? 0)}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3"
                        onClick={() => {
                          const params = new URLSearchParams(location.search);
                          params.set('tab', 'requests');
                          navigate(`${location.pathname}?${params.toString()}`);
                        }}
                      >
                        Gestionează
                      </Button>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-4">
                      <div className="text-xs text-muted-foreground">Profesori</div>
                      <div className="mt-1 text-2xl font-semibold text-foreground">
                        {summaryLoading ? '—' : String(summary?.teachers ?? 0)}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3"
                        onClick={() => {
                          const params = new URLSearchParams(location.search);
                          params.set('tab', 'users');
                          navigate(`${location.pathname}?${params.toString()}`);
                        }}
                      >
                        Utilizatori
                      </Button>
                    </div>
                  </div>

                  {summaryError ? <div className="text-sm text-error">{summaryError}</div> : null}

                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Acțiuni rapide</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                          Operațional: înscrieri/transferuri, dosare digitale, registratură, adeverințe, rapoarte.
                        </p>
                      </div>
                      <Button variant="outline" iconName="RefreshCw" onClick={() => void loadSummary()} loading={summaryLoading}>
                        Reîncarcă
                      </Button>
                    </div>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      <Button
                        variant="outline"
                        iconName="Users"
                        onClick={() => {
                          const params = new URLSearchParams(location.search);
                          params.set('tab', 'users');
                          navigate(`${location.pathname}?${params.toString()}`);
                        }}
                      >
                        Creează conturi
                      </Button>
                      <Button
                        variant="outline"
                        iconName="School"
                        onClick={() => {
                          const params = new URLSearchParams(location.search);
                          params.set('tab', 'classes');
                          navigate(`${location.pathname}?${params.toString()}`);
                        }}
                      >
                        Gestionează clase
                      </Button>
                      <Button
                        variant="outline"
                        iconName="Archive"
                        onClick={() => {
                          const params = new URLSearchParams(location.search);
                          params.set('tab', 'archive');
                          navigate(`${location.pathname}?${params.toString()}`);
                        }}
                      >
                        Caută în arhivă
                      </Button>
                      <Button
                        variant="outline"
                        iconName="ClipboardList"
                        onClick={() => {
                          const params = new URLSearchParams(location.search);
                          params.set('tab', 'registry');
                          navigate(`${location.pathname}?${params.toString()}`);
                        }}
                      >
                        Registratură
                      </Button>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Ultimele arhivări</h2>
                        <p className="text-sm text-muted-foreground mt-1">Acces rapid la cataloagele arhivate recent.</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const params = new URLSearchParams(location.search);
                          params.set('tab', 'archive');
                          navigate(`${location.pathname}?${params.toString()}`);
                        }}
                      >
                        Vezi toate
                      </Button>
                    </div>
                    <div className="mt-4 space-y-2">
                      {summaryLoading ? <div className="text-sm text-muted-foreground">Se încarcă…</div> : null}
                      {!summaryLoading && (!summary?.recent_archived_catalogs || summary.recent_archived_catalogs.length === 0) ? (
                        <div className="text-sm text-muted-foreground">Nu există arhivări încă.</div>
                      ) : null}
                      {(summary?.recent_archived_catalogs || []).map((r) => {
                        const displayName = String(r.archived_class_name || r.classroom_name || '').trim() || `Clasa #${r.classroom_id}`;
                        return (
                          <button
                            key={String(r.config_id)}
                            type="button"
                            className="w-full text-left border border-border rounded-lg p-3 bg-background hover:bg-muted/20 transition-colors"
                            onClick={() => {
                              const params = new URLSearchParams(location.search);
                              params.set('tab', 'classes');
                              params.set('classId', String(r.classroom_id));
                              params.set('classTab', 'catalog');
                              params.set('catalogYear', String(r.school_year || '').trim());
                              navigate(`${location.pathname}?${params.toString()}`);
                            }}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-foreground truncate">{displayName}</div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  {r.school_year} • {r.structure} • {r.education_level}
                                </div>
                              </div>
                              <Icon name="ChevronRight" size={18} className="text-muted-foreground" />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}

              {tab === 'classes' ? (
                classDetailId ? (
                  <div className="space-y-4">
                    <div className="bg-card border border-border rounded-lg p-6">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={closeClassDetail}
                              className="p-2 rounded-md hover:bg-muted transition-colors"
                              aria-label="Înapoi"
                            >
                              <Icon name="ChevronLeft" size={18} />
                            </button>
                            <div className="min-w-0 flex-1">
                              <h2 className="text-lg font-semibold text-foreground truncate">{classDetail?.name || `Clasa #${classDetailId}`}</h2>
                              {classDetail?.archived_at ? (
                                <div className="text-xs text-muted-foreground mt-1">
                                  Arhivat: {classDetail.archived_school_year || '—'} • classroom: #{classDetailId}
                                </div>
                              ) : (
                                <div className="text-xs text-muted-foreground mt-1">
                                  catalog: #{classDetail?.school_class_id || '—'} • classroom: #{classDetailId}
                                  {classDetail?.admin_professor_name ? (
                                    <span>
                                      {' '}
                                      • Diriginte: {String(classDetail.admin_professor_name).trim()}
                                    </span>
                                  ) : (
                                    <span className="text-warning">
                                      {' '}
                                      • Fără diriginte
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {classDetail && !classDetail.archived_at ? (
                            <Button variant="outline" iconName="Pencil" onClick={() => openRenameClass(classDetail)}>
                              Redenumește
                            </Button>
                          ) : null}
                          <Button variant="outline" iconName="RefreshCw" onClick={() => void loadClasses()} loading={classesLoading}>
                            Reîncarcă
                          </Button>
                        </div>
                      </div>

                      <div className="mt-4 flex items-center gap-2 flex-wrap">
                        <Button
                          variant={classDetailTab === 'catalog' ? 'default' : 'outline'}
                          iconName="BookOpen"
                          iconPosition="left"
                          onClick={() => setClassDetailTab('catalog')}
                        >
                          Catalog
                        </Button>
                        <Button
                          variant={classDetailTab === 'students' ? 'default' : 'outline'}
                          iconName="Users"
                          iconPosition="left"
                          onClick={() => setClassDetailTab('students')}
                        >
                          Elevi
                        </Button>
                        <Button
                          variant={classDetailTab === 'teachers' ? 'default' : 'outline'}
                          iconName="GraduationCap"
                          iconPosition="left"
                          onClick={() => setClassDetailTab('teachers')}
                        >
                          Profesori
                        </Button>
                        <Button
                          variant={classDetailTab === 'documents' ? 'default' : 'outline'}
                          iconName="FileText"
                          iconPosition="left"
                          onClick={() => setClassDetailTab('documents')}
                        >
                          Documente
                        </Button>
                      </div>
                    </div>

                    {classDetailTab === 'catalog' ? (
                      <CatalogPanel classroomId={classDetailId} initialSchoolYear={classDetailCatalogYear} />
                    ) : null}

                    {classDetailTab === 'teachers' ? (
                      <div className="space-y-4">
                        {classDetail && !classDetail.archived_at ? (
                          <div className="bg-card border border-border rounded-lg p-4">
                            <div className="flex items-end gap-2 flex-wrap">
                              <div className="min-w-[320px] flex-1">
                                <Select
                                  label="Setează diriginte (profesor admin)"
                                  options={classAdminTeacherOptions}
                                  value={detailAdminTeacherId}
                                  disabled={detailAdminBusy || teachersLoading || (teachers || []).length === 0}
                                  onChange={(v) => setDetailAdminTeacherId(String(v || ''))}
                                  placeholder={(teachers || []).length === 0 ? 'Nu există profesori' : 'Selectează dirigintele'}
                                />
                              </div>
                              <Button onClick={() => void setClassAdmin()} disabled={detailAdminBusy || !detailAdminTeacherId}>
                                Setează
                              </Button>
                            </div>
                            {detailAdminInfo ? <div className="mt-2 text-xs text-success">{detailAdminInfo}</div> : null}
                            {detailAdminError ? <div className="mt-2 text-xs text-error">{detailAdminError}</div> : null}
                          </div>
                        ) : null}
                        <ProfesoriPanel key={`${classDetailId}-${teachersPanelNonce}`} classroomId={classDetailId} />
                      </div>
                    ) : null}

                    {classDetailTab === 'students' ? (
                      <div className="bg-card border border-border rounded-lg p-6">
                        <div className="flex items-center justify-between gap-3 mb-4">
                          <h3 className="text-lg font-semibold text-foreground">Elevi</h3>
                          <Button variant="outline" iconName="RefreshCw" onClick={() => void loadDetailStudents()} loading={detailStudentsLoading}>
                            Reîncarcă
                          </Button>
                        </div>
                        {detailStudentsError ? (
                          <div className="mb-4 bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{detailStudentsError}</div>
                        ) : null}
                        {detailStudentsLoading ? (
                          <div className="text-sm text-muted-foreground">Se încarcă…</div>
                        ) : detailStudents.length === 0 ? (
                          <div className="text-sm text-muted-foreground">Nu există elevi în această clasă.</div>
                        ) : (
                          <div className="divide-y divide-border">
                            {detailStudents.map((s) => (
                              <div key={s.id} className="py-3 flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-foreground truncate">{s.full_name}</div>
                                  <div className="text-xs text-muted-foreground truncate">{s.email}</div>
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {s.over16 ? '16+' : '<16'} • {s.phone || '—'}
                                  </div>
                                </div>
                                <div className="text-xs text-muted-foreground text-right">
                                  <div>Mamă: {s.parent_email1 || '—'}</div>
                                  <div>Tată: {s.parent_email2 || '—'}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="mt-4 text-xs text-muted-foreground">
                          Pentru adăugare elevi, folosește tab-ul „Utilizatori”.
                        </div>
                      </div>
                    ) : null}

                    {classDetailTab === 'documents' ? (
                      <div className="bg-card border border-border rounded-lg p-6">
                        <div className="text-sm text-muted-foreground">În curând: exporturi PDF (adeverințe, foi matricole, etc.).</div>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Clase (B2B)</h2>
                        <p className="text-sm text-muted-foreground mt-1">Creează clase sincronizate 1:1 (catalog + classroom).</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="inline-flex rounded-md border border-border overflow-hidden">
                          <button
                            type="button"
                            className={`px-3 py-2 text-sm ${
                              classesView === 'active' ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground hover:bg-muted'
                            }`}
                            onClick={() => setClassesView('active')}
                          >
                            Active
                          </button>
                          <button
                            type="button"
                            className={`px-3 py-2 text-sm ${
                              classesView === 'archived' ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground hover:bg-muted'
                            }`}
                            onClick={() => setClassesView('archived')}
                          >
                            Arhivă
                          </button>
                        </div>
                        <Button variant="outline" iconName="RefreshCw" onClick={() => void loadClasses()} loading={classesLoading}>
                          Reîncarcă
                        </Button>
                      </div>
                    </div>

                    {classesError ? <div className="mt-3 text-sm text-error">{classesError}</div> : null}

                    {classesView === 'active' ? (
                      <>
                        <div className="mt-4 flex items-end gap-2 flex-wrap">
                        <div className="min-w-[260px] flex-1">
                          <Input
                            label="Nume clasă"
                            placeholder='Ex: "Clasa a VII-a C"'
                            value={className}
                            onChange={(e) => setClassName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void createClass();
                            }}
                          />
                        </div>
                        <div className="min-w-[320px] flex-1">
                          <Select
                            label="Diriginte (profesor admin)"
                            options={classAdminTeacherOptions}
                            value={classAdminTeacherId}
                            disabled={teachersLoading || (teachers || []).length === 0}
                            onChange={(v) => setClassAdminTeacherId(String(v || ''))}
                            placeholder={(teachers || []).length === 0 ? 'Nu există profesori' : 'Selectează dirigintele'}
                          />
                        </div>
                        <Button iconName="Plus" onClick={() => void createClass()} disabled={!canCreateClass}>
                          Creează
                        </Button>
                      </div>
                      {(!teachersLoading && (teachers || []).length === 0) ? (
                        <div className="mt-2 text-xs text-muted-foreground">
                          Nu există profesori în școală. Poți crea clasa, dar până setezi un diriginte, postările elevilor
                          pot necesita moderare de către secretariat.
                        </div>
                      ) : null}
                      {(!teachersLoading && (teachers || []).length > 0 && !classAdminTeacherId) ? (
                        <div className="mt-2 text-xs text-muted-foreground">
                          Dirigintele (profesor admin) este obligatoriu dacă există profesori în școală.
                        </div>
                      ) : null}
                      </>
                    ) : (
                      <div className="mt-4 text-sm text-muted-foreground">
                        Arhiva este read-only. Clasele arhivate sunt păstrate pentru istoric și nu se mai modifică.
                      </div>
                    )}

                    <div className="mt-4 space-y-2 max-h-[520px] overflow-auto pr-1">
                      {classesLoading && classes.length === 0 ? <div className="text-sm text-muted-foreground">Se încarcă…</div> : null}
                      {!classesLoading && classes.length === 0 ? (
                        <div className="text-sm text-muted-foreground">
                          {classesView === 'active'
                            ? 'Nu există clase sau școala nu este în modul B2B.'
                            : 'Nu există clase arhivate.'}
                        </div>
                      ) : null}
                      {classes.map((c) => (
                        <div
                          key={`${c.classroom_id}-${c.school_class_id}`}
                          className="border border-border rounded-lg p-3 bg-background hover:bg-muted/20 transition-colors"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <button
                              type="button"
                              className="min-w-0 flex-1 text-left"
                              onClick={() => openClassDetail(Number(c.classroom_id))}
                            >
                              <div className="text-sm font-medium text-foreground truncate">{c.name}</div>
                              {c.archived_at ? (
                                <div className="text-xs text-muted-foreground mt-1">
                                  Arhivat: {c.archived_school_year || '—'}
                                </div>
                              ) : (
                                <div className="text-xs text-muted-foreground mt-1">
                                  catalog: #{c.school_class_id} • classroom: #{c.classroom_id}
                                  {c.admin_professor_name ? (
                                    <span>
                                      {' '}
                                      • Diriginte: {String(c.admin_professor_name).trim()}
                                    </span>
                                  ) : (
                                    <span className="text-warning">
                                      {' '}
                                      • Fără diriginte
                                    </span>
                                  )}
                                </div>
                              )}
                            </button>

                            {!c.archived_at ? (
                              <div className="relative">
                              <button
                                type="button"
                                className="p-2 rounded-md hover:bg-muted transition-colors"
                                aria-label="Acțiuni"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const nextId = Number(c.classroom_id);
                                  const isOpen = openClassMenuId === nextId;
                                  if (isOpen) {
                                    setOpenClassMenuId(null);
                                    setClassMenuPos(null);
                                    return;
                                  }

                                  const menuWidth = 192;
                                  const menuHeight = 96;
                                  const padding = 8;
                                  const gap = 6;

                                  const rect = e.currentTarget.getBoundingClientRect();
                                  let left = rect.right - menuWidth;
                                  left = Math.max(padding, Math.min(window.innerWidth - menuWidth - padding, left));

                                  const openUp = rect.bottom + gap + menuHeight + padding > window.innerHeight;
                                  let top = openUp ? rect.top - gap - menuHeight : rect.bottom + gap;
                                  top = Math.max(padding, Math.min(window.innerHeight - menuHeight - padding, top));

                                  setClassMenuPos({ top, left });
                                  setOpenClassMenuId(nextId);
                                }}
                              >
                                <Icon name="MoreVertical" size={18} className="text-muted-foreground" />
                              </button>
                              {openClassMenuId === Number(c.classroom_id) && classMenuPos
                                ? createPortal(
                                    <div
                                      className="fixed w-48 bg-card border border-border rounded-lg shadow-lg z-[9999] overflow-hidden"
                                      style={{ top: classMenuPos.top, left: classMenuPos.left }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <button
                                        type="button"
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                                        onClick={() => {
                                          setOpenClassMenuId(null);
                                          setClassMenuPos(null);
                                          openRenameClass(c);
                                        }}
                                      >
                                        Redenumește
                                      </button>
                                      <button
                                        type="button"
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                                        onClick={() => {
                                          setOpenClassMenuId(null);
                                          setClassMenuPos(null);
                                          openArchiveClass(c);
                                        }}
                                      >
                                        Arhivează
                                      </button>
                                    </div>,
                                    document.body
                                  )
                                : null}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ) : null}

              {tab === 'archive' ? (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">Arhivă cataloage</h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        Cataloagele arhivate sunt read-only. Poți căuta după elev (nume/email) sau după numele clasei.
                      </p>
                    </div>
                    <Button variant="outline" iconName="RefreshCw" onClick={() => void loadArchiveCatalogs()} loading={archiveCatalogsLoading}>
                      Reîncarcă
                    </Button>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      label="Caută"
                      placeholder="Ex: elev1@... / Popescu / Clasa a XI-a A"
                      value={archiveCatalogsQuery}
                      onChange={(e) => setArchiveCatalogsQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void loadArchiveCatalogs();
                      }}
                    />
                    <Select
                      label="An școlar"
                      value={archiveCatalogsYear ?? ''}
                      options={archiveYearOptions}
                      onChange={(v) => setArchiveCatalogsYear(v ? String(v) : null)}
                      clearable
                    />
                  </div>

                  {archiveCatalogsError ? <div className="mt-3 text-sm text-error">{archiveCatalogsError}</div> : null}

                  <div className="mt-4 space-y-2 max-h-[520px] overflow-auto pr-1">
                    {archiveCatalogsLoading && archiveCatalogs.length === 0 ? (
                      <div className="text-sm text-muted-foreground">Se încarcă…</div>
                    ) : null}
                    {!archiveCatalogsLoading && archiveCatalogs.length === 0 ? (
                      <div className="text-sm text-muted-foreground">Nu există cataloage arhivate.</div>
                    ) : null}
                    {archiveCatalogs.map((r) => {
                      const archivedName = String(r.archived_class_name || '').trim();
                      const displayName = archivedName || String(r.classroom_name || '').trim();
                      const currentName = String(r.classroom_name || '').trim();
                      const showNow = archivedName && currentName && archivedName !== currentName;
                      return (
                        <button
                          key={String(r.config_id)}
                          type="button"
                          className="w-full text-left border border-border rounded-lg p-3 bg-background hover:bg-muted/20 transition-colors"
                          onClick={() => {
                            const params = new URLSearchParams(location.search);
                            params.set('tab', 'classes');
                            params.set('classId', String(r.classroom_id));
                            params.set('classTab', 'catalog');
                            params.set('catalogYear', String(r.school_year || '').trim());
                            navigate(`${location.pathname}?${params.toString()}`);
                          }}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-foreground truncate">{displayName}</div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {r.school_year} • {r.structure} • {r.education_level}
                              </div>
                              {showNow ? (
                                <div className="text-xs text-muted-foreground mt-1">Acum: {currentName}</div>
                              ) : null}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-foreground border border-border">
                                Arhivat
                              </span>
                              {r.class_archived_at ? (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-foreground border border-border">
                                  Clasă arhivată
                                </span>
                              ) : null}
                              <Icon name="ChevronRight" size={18} className="text-muted-foreground" />
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-6 pt-6 border-t border-border">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">{`Elevi absolvi\u021Bi`}</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                          {`Elevii absolvi\u021Bi sunt arhiva\u021Bi (login blocat) \u0219i r\u0103m\u00E2n \u00EEn platform\u0103 pentru audit.`}
                        </p>
                      </div>
                      <Button variant="outline" iconName="RefreshCw" onClick={() => void loadArchiveGraduates()} loading={archiveGraduatesLoading}>
                        {`Re\u00EEnc\u0103rc\u0103`}
                      </Button>
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        label={`Caut\u0103 elev`}
                        placeholder="Nume / email"
                        value={archiveGraduatesQuery}
                        onChange={(e) => setArchiveGraduatesQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void loadArchiveGraduates();
                        }}
                      />
                      <Select
                        label={`Clas\u0103`}
                        value={archiveGraduatesClassroomId ?? ''}
                        options={dossierClassOptions}
                        onChange={(v) => setArchiveGraduatesClassroomId(v ? Number(v) : null)}
                        searchable
                        clearable
                      />
                    </div>

                    {archiveGraduatesError ? <div className="mt-3 text-sm text-error">{archiveGraduatesError}</div> : null}

                    <div className="mt-4 space-y-2 max-h-[520px] overflow-auto pr-1">
                      {archiveGraduatesLoading && archiveGraduates.length === 0 ? (
                        <div className="text-sm text-muted-foreground">{`Se \u00EEnc\u0103rc\u0103\u2026`}</div>
                      ) : null}
                      {!archiveGraduatesLoading && archiveGraduates.length === 0 ? (
                        <div className="text-sm text-muted-foreground">{`Nu exist\u0103 elevi absolvi\u021Bi.`}</div>
                      ) : null}
                      {archiveGraduates.map((s) => (
                        <div key={String(s.id)} className="border border-border rounded-lg p-3 bg-background">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-foreground truncate">{s.full_name}</div>
                              <div className="text-xs text-muted-foreground mt-1">{s.email || '—'}</div>
                              <div className="text-xs text-muted-foreground mt-1">{s.classroom_name || '—'}</div>
                              <div className="text-xs text-muted-foreground mt-1">
                                Absolvit: {s.graduated_school_year || '—'}
                                {s.graduated_at ? ` • ${new Date(s.graduated_at).toLocaleString('ro-RO')}` : ''}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap justify-start sm:justify-end w-full sm:w-auto">
                              <Button
                                variant="outline"
                                className="w-full sm:w-auto"
                                size="sm"
                                iconName="Download"
                                className="w-full sm:w-auto"
                                onClick={() => void exportUserJson(Number(s.id))}
                              >
                                Export
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}

              {tab === 'requests' ? (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">Cereri aderare (B2B)</h2>
                      <p className="text-sm text-muted-foreground mt-1">Profesori care cer acces la clasele școlii.</p>
                    </div>
                    <Button variant="outline" iconName="RefreshCw" onClick={() => void loadRequests()} loading={requestsLoading}>
                      Reîncarcă
                    </Button>
                  </div>

                  {requestsError ? <div className="mt-3 text-sm text-error">{requestsError}</div> : null}

                  <div className="mt-4 space-y-2 max-h-[520px] overflow-auto pr-1">
                    {requestsLoading && requests.length === 0 ? <div className="text-sm text-muted-foreground">Se încarcă…</div> : null}
                    {!requestsLoading && requests.length === 0 ? <div className="text-sm text-muted-foreground">Nu există cereri pending.</div> : null}
                    {requests.map((r) => (
                      <div key={r.id} className="border border-border rounded-lg p-3 bg-background flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-foreground">{r.classroom_name}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {r.requester?.full_name || r.requester?.email || `User #${r.requester?.id || 0}`} • {r.requester?.role || '—'}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => void rejectRequest(r.id)}>
                            Respinge
                          </Button>
                          <Button size="sm" onClick={() => void acceptRequest(r.id)}>
                            Acceptă
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {tab === 'users' ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Profesori</h2>
                        <p className="text-sm text-muted-foreground mt-1">Conturi create de secretariat (parolă pe email).</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" iconName="RefreshCw" onClick={() => void loadTeachers()} loading={teachersLoading}>
                          Reîncarcă
                        </Button>
                        <Button iconName="UserPlus" onClick={() => setShowAddTeacherModal(true)}>
                          Adaugă
                        </Button>
                      </div>
                    </div>

                    {teachersError ? <div className="mt-3 text-sm text-error">{teachersError}</div> : null}

                    <div className="mt-4 overflow-auto">
                      <table className="min-w-[520px] w-full text-sm">
                        <thead className="bg-muted/40">
                          <tr>
                            <th className="p-3 text-left text-muted-foreground font-medium">Profesor</th>
                            <th className="p-3 text-right text-muted-foreground font-medium">Acțiuni</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {teachersLoading && teachers.length === 0 ? (
                            <tr>
                              <td className="p-3 text-muted-foreground" colSpan={2}>
                                Se încarcă…
                              </td>
                            </tr>
                          ) : null}
                          {!teachersLoading && teachers.length === 0 ? (
                            <tr>
                              <td className="p-3 text-muted-foreground" colSpan={2}>
                                Nu există profesori.
                              </td>
                            </tr>
                          ) : null}
                          {teachers.map((t) => (
                            <tr key={t.id} className="hover:bg-muted/30">
                              <td className="p-3">
                                <div className="text-foreground font-medium">{t.full_name}</div>
                                <div className="text-xs text-muted-foreground">{t.email}</div>
                                {t.phone ? <div className="text-xs text-muted-foreground">{t.phone}</div> : null}
                              </td>
                              <td className="p-3 text-right">
                                <div className="flex items-center justify-end gap-2 flex-wrap">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    iconName="Edit"
                                    onClick={() =>
                                      void editUserBasic({
                                        id: t.id,
                                        full_name: t.full_name,
                                        email: t.email,
                                        phone: t.phone ?? null,
                                        after: () => void loadTeachers(),
                                      })
                                    }
                                  >
                                    Edit
                                  </Button>
                                  <Button variant="outline" size="sm" iconName="Download" onClick={() => void exportUserJson(t.id)}>
                                    Export
                                  </Button>
                                  <Button variant="outline" size="sm" iconName="Send" onClick={() => void resendTeacherInvite(t.id)}>
                                    Retrimite
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Elevi & părinți</h2>
                        <p className="text-sm text-muted-foreground mt-1">Folosește același formular ca în contul profesorului.</p>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                        <Button
                          variant="outline"
                          iconName="RefreshCw"
                          onClick={() => selectedClassroomId && loadStudents(selectedClassroomId)}
                          disabled={!selectedClassroomId}
                          className="w-full sm:w-auto"
                        >
                          Reîncarcă
                        </Button>
                        <Button
                          variant="outline"
                          iconName="GraduationCap"
                          onClick={() => void graduateSelectedClassroom()}
                          disabled={!selectedClassroomId || studentsLoading || classGraduateBusy}
                          loading={classGraduateBusy}
                          className="w-full sm:w-auto"
                        >
                          Absolvire clasă
                        </Button>
                        <Button
                          iconName="UserPlus"
                          onClick={() => setShowAddStudentModal(true)}
                          disabled={!selectedClassroomId}
                          className="w-full sm:w-auto"
                        >
                          Adaugă elev
                        </Button>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Select
                        label="Clasă"
                        value={selectedClassroomId ?? ''}
                        options={classroomOptions}
                        onChange={(v) => setSelectedClassroomId(v ? Number(v) : null)}
                        searchable
                        clearable
                      />
                      <div className="text-xs text-muted-foreground md:pt-7">
                        Selectează clasa, apoi adaugă elevul în clasa respectivă.
                        <label className="mt-2 flex items-center gap-2 text-xs select-none">
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={() => void goToArchiveGraduates()}
                          />
                          Vezi elevii absolviți în Arhivă
                        </label>
                      </div>
                    </div>

                    {studentsError ? <div className="mt-3 text-sm text-error">{studentsError}</div> : null}

                    <div className="mt-4 space-y-2 max-h-[420px] overflow-auto pr-1">
                      {studentsLoading && students.length === 0 ? <div className="text-sm text-muted-foreground">Se încarcă…</div> : null}
                      {!studentsLoading && selectedClassroomId && students.length === 0 ? (
                        <div className="text-sm text-muted-foreground">Nu există elevi în această clasă.</div>
                      ) : null}
                      {students.map((s) => (
                        <div key={s.id} className="border border-border rounded-lg p-3 bg-background">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="text-sm font-medium text-foreground">{s.full_name}</div>
                                {(s as any)?.is_graduated ? (
                                  <span className="text-[11px] px-2 py-0.5 rounded-md border border-warning/30 text-warning bg-warning/10">Absolvit</span>
                                ) : null}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {s.over16 ? '16+' : '<16'} • {s.email}
                              </div>
                            </div>
                            <div className="flex flex-col items-stretch sm:items-end gap-2 w-full sm:w-auto">
                              <div className="grid grid-cols-1 min-[420px]:grid-cols-2 sm:flex sm:flex-wrap items-stretch sm:items-center justify-stretch sm:justify-end gap-2 w-full sm:w-auto">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  iconName="Send"
                                  onClick={() => void resendStudentInvite(Number(s.id))}
                                  disabled={studentsLoading}
                                  className="w-full sm:w-auto"
                                >
                                  Retrimite
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  iconName="Edit"
                                  onClick={() =>
                                    void editUserBasic({
                                      id: Number(s.id),
                                      full_name: s.full_name,
                                      email: s.email,
                                      phone: (s as any).phone ?? null,
                                      after: () => {
                                        if (selectedClassroomId) void loadStudents(selectedClassroomId);
                                      },
                                    })
                                  }
                                  disabled={studentsLoading}
                                  className="w-full sm:w-auto"
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  iconName="Download"
                                  onClick={() => void exportUserJson(Number(s.id))}
                                  disabled={studentsLoading}
                                  className="w-full sm:w-auto"
                                >
                                  Export
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  iconName="GraduationCap"
                                  onClick={() =>
                                    void graduateUserAccount({
                                      id: Number(s.id),
                                      label: s.full_name,
                                      after: () => {
                                        if (selectedClassroomId) void loadStudents(selectedClassroomId);
                                      },
                                    })
                                  }
                                  loading={!!studentGraduateLoading[Number(s.id)]}
                                  disabled={studentsLoading || !!studentGraduateLoading[Number(s.id)] || !!(s as any)?.is_graduated}
                                  className="w-full sm:w-auto"
                                >
                                  Absolvire
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  iconName="Trash2"
                                  onClick={() =>
                                    void deleteUserAccount({
                                      id: Number(s.id),
                                      label: s.full_name,
                                      after: () => {
                                        if (selectedClassroomId) void loadStudents(selectedClassroomId);
                                      },
                                    })
                                  }
                                  disabled={studentsLoading}
                                  className="w-full sm:w-auto"
                                >
                                  Sterge
                                </Button>
                              </div>
                              <div className="text-xs text-muted-foreground">{s.phone || ''}</div>
                            </div>
                          </div>
                          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-muted-foreground">
                            <div>Mamă: {s.parent_email1 || '—'}</div>
                            <div>Tată: {s.parent_email2 || '—'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}

              {tab === 'students' ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Elevi</h2>
                        <p className="text-sm text-muted-foreground mt-1">Caută elevul și deschide dosarul digital.</p>
                      </div>
                      <Button variant="outline" iconName="RefreshCw" onClick={() => void loadDossierStudents()} loading={dossierLoading}>
                        Reîncarcă
                      </Button>
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        label="Caută elev"
                        placeholder="Nume / email"
                        value={dossierQuery}
                        onChange={(e) => setDossierQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void loadDossierStudents();
                        }}
                      />
                      <Select
                        label="Clasă"
                        value={dossierClassroomId ?? ''}
                        options={dossierClassOptions}
                        onChange={(v) => setDossierClassroomId(v ? Number(v) : null)}
                        searchable
                        clearable
                      />
                    </div>

                    <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground select-none">
                      <input
                        type="checkbox"
                        checked={false}
                        onChange={() => void goToArchiveGraduates()}
                      />
                      Vezi elevii absolviți în Arhivă
                    </label>

                    {dossierError ? <div className="mt-3 text-sm text-error">{dossierError}</div> : null}

                    <div className="mt-4 space-y-2 max-h-[520px] overflow-auto pr-1">
                      {dossierLoading && dossierStudents.length === 0 ? (
                        <div className="text-sm text-muted-foreground">Se încarcă…</div>
                      ) : null}
                      {!dossierLoading && dossierStudents.length === 0 ? (
                        <div className="text-sm text-muted-foreground">Nu există elevi pentru filtrul curent.</div>
                      ) : null}
                      {dossierStudents.map((s) => (
                        <button
                          key={String(s.id)}
                          type="button"
                          className={`w-full text-left border rounded-lg p-3 transition-colors ${
                            Number(dossierSelectedStudentId) === Number(s.id) ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'
                          }`}
                          onClick={() => setDossierSelectedStudentId(Number(s.id))}
                        >
                          <div className="text-sm font-medium text-foreground truncate">{s.full_name}</div>
                          <div className="text-xs text-muted-foreground mt-1 truncate">{s.email || '—'}</div>
                          <div className="text-xs text-muted-foreground mt-1 truncate">{s.classroom_name || '—'}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Dosar digital</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                          {dossierSelectedStudent ? (
                            <>
                              {dossierSelectedStudent.full_name} • {dossierSelectedStudent.classroom_name || '—'}
                            </>
                          ) : (
                            'Selectează un elev din listă.'
                          )}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        iconName="RefreshCw"
                        onClick={() => (dossierSelectedStudentId ? loadDossierDocs(dossierSelectedStudentId) : null)}
                        disabled={!dossierSelectedStudentId}
                        loading={dossierDocsLoading}
                      >
                        Reîncarcă
                      </Button>
                    </div>

                    {dossierDocsError ? <div className="mt-3 text-sm text-error">{dossierDocsError}</div> : null}

                    <div className="mt-4 border border-border rounded-lg p-4 bg-background">
                      <div className="text-sm font-semibold text-foreground">Încarcă document</div>
                      {dossierUploadError ? <div className="mt-2 text-sm text-error">{dossierUploadError}</div> : null}
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Select
                          label="Tip document"
                          value={dossierDocType}
                          options={[
                            { value: 'dosar_inscriere', label: 'Dosar înscriere' },
                            { value: 'certificat_nastere', label: 'Certificat naștere' },
                            { value: 'ci_parinte', label: 'CI părinte' },
                            { value: 'adeverinta_medicala', label: 'Adeverință medicală' },
                            { value: 'cerere_transfer', label: 'Cerere transfer' },
                            { value: 'altele', label: 'Altele' },
                          ]}
                          onChange={(v) => setDossierDocType(String(v || 'dosar_inscriere'))}
                        />
                        <Input label="Titlu (opțional)" value={dossierDocTitle} onChange={(e) => setDossierDocTitle(e.target.value)} />
                      </div>
                      <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
                        <input
                          type="file"
                          disabled={!dossierSelectedStudentId || dossierUploadBusy}
                          className="w-full sm:max-w-[320px]"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            void submitDossierUpload(f);
                            e.currentTarget.value = '';
                          }}
                        />
                        <div className="text-xs text-muted-foreground sm:ml-auto">PDF/JPG/PNG/DOCX, max 10MB.</div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-foreground">Documente</div>
                        <div className="text-xs text-muted-foreground">{dossierDocs.length}</div>
                      </div>
                      <div className="mt-3 space-y-2">
                        {dossierDocsLoading && dossierDocs.length === 0 ? (
                          <div className="text-sm text-muted-foreground">Se încarcă…</div>
                        ) : null}
                        {!dossierDocsLoading && dossierSelectedStudentId && dossierDocs.length === 0 ? (
                          <div className="text-sm text-muted-foreground">Nu există documente încă.</div>
                        ) : null}
                        {!dossierSelectedStudentId ? (
                          <div className="text-sm text-muted-foreground">Selectează un elev pentru a vedea dosarul.</div>
                        ) : null}
                        {dossierDocs.map((d) => (
                          <div key={String(d.id)} className="border border-border rounded-lg p-3 bg-background">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-foreground truncate">{d.title || d.original_name}</div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  {(d.doc_type || '—') + (d.created_at ? ` • ${new Date(d.created_at).toLocaleString()}` : '')}
                                </div>
                              </div>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => window.open(getSecretariatStudentDocumentDownloadUrl(d.id), '_blank')}
                                  className="w-full sm:w-auto"
                                >
                                  Descarcă
                                </Button>
                                <Button size="sm" variant="danger" onClick={() => void deleteDossierDoc(d.id)} className="w-full sm:w-auto">
                                  Șterge
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {tab === 'transfers' ? (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">Transferuri elevi</h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        Trimiți către altă școală un export read-only al dosarului (note, absențe, documente). Școala de destinație acceptă sau respinge.
                      </p>
                    </div>
                    <Button variant="outline" iconName="RefreshCw" onClick={() => void loadTransfers()} loading={transferLoading}>
                      Reîncarcă
                    </Button>
                  </div>

                  {(transferError || transferSchoolsError) ? (
                    <div className="mt-3 text-sm text-error">{transferError || transferSchoolsError}</div>
                  ) : null}

                  <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="border border-border rounded-lg p-4 bg-background">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-sm font-semibold text-foreground">Inițiază transfer</div>
                        {transferSchoolsLoading ? <div className="text-xs text-muted-foreground">Se încarcă școlile…</div> : null}
                      </div>

                      {transferCreateError ? <div className="mt-2 text-sm text-error">{transferCreateError}</div> : null}

                      <div className="mt-3 space-y-3">
                        <div>
                          <Input
                            label="Elev (căutare)"
                            placeholder="Nume / email"
                            value={transferStudentQuery}
                            onChange={(e) => {
                              setTransferStudentQuery(e.target.value);
                              setTransferStudentId(null);
                            }}
                          />
                          {transferStudentResultsLoading ? <div className="mt-2 text-xs text-muted-foreground">Se caută…</div> : null}
                          {transferStudentQuery.trim() && transferStudentResults.length > 0 ? (
                            <div className="mt-2 max-h-44 overflow-auto border border-border rounded-lg">
                              {transferStudentResults.map((s) => (
                                <button
                                  key={String(s.id)}
                                  type="button"
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                                    transferStudentId === Number(s.id) ? 'bg-primary/5' : ''
                                  }`}
                                  onClick={() => {
                                    setTransferStudentId(Number(s.id));
                                    setTransferStudentQuery(String(s.full_name || '').trim() || String(s.email || '').trim());
                                    setTransferStudentResults([]);
                                  }}
                                >
                                  <div className="font-medium text-foreground truncate">{s.full_name}</div>
                                  <div className="text-xs text-muted-foreground truncate">{s.email || '—'}</div>
                                  <div className="text-xs text-muted-foreground truncate">{s.classroom_name || '—'}</div>
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </div>

                        <Select
                          label="Școala de destinație"
                          value={transferToSchoolId ?? ''}
                          options={transferSchoolOptions}
                          onChange={(v) => setTransferToSchoolId(v ? Number(v) : null)}
                          searchable
                          clearable
                        />

                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Mesaj (opțional)</label>
                          <textarea
                            value={transferMessage}
                            onChange={(e) => setTransferMessage(e.target.value)}
                            className="w-full min-h-[90px] px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="Ex: Transfer pentru înscriere în anul școlar curent…"
                          />
                        </div>

                        <Button
                          iconName="ArrowLeftRight"
                          onClick={() => void submitCreateTransfer()}
                          loading={transferCreateBusy}
                          disabled={transferCreateBusy || transferStudentResultsLoading}
                        >
                          Trimite transfer
                        </Button>
                      </div>
                    </div>

                    <div className="border border-border rounded-lg p-4 bg-background">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-sm font-semibold text-foreground">Transferuri primite</div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setTransferIncomingPage((p) => Math.max(1, p - 1))}
                            disabled={transferIncomingPage <= 1}
                          >
                            Prev
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setTransferIncomingPage((p) => Math.min(transferIncomingTotalPages, p + 1))}
                            disabled={transferIncomingPage >= transferIncomingTotalPages}
                          >
                            Next
                          </Button>
                        </div>
                      </div>

                      <div className="mt-3 overflow-auto">
                        <table className="min-w-[760px] w-full text-sm">
                          <thead className="bg-muted/40">
                            <tr>
                              <th className="p-3 text-left text-muted-foreground font-medium">Data</th>
                              <th className="p-3 text-left text-muted-foreground font-medium">Elev</th>
                              <th className="p-3 text-left text-muted-foreground font-medium">Din școala</th>
                              <th className="p-3 text-left text-muted-foreground font-medium">Status</th>
                              <th className="p-3 text-right text-muted-foreground font-medium">Acțiuni</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {transferLoading && transferIncoming.length === 0 ? (
                              <tr>
                                <td className="p-3 text-muted-foreground" colSpan={5}>
                                  Se încarcă…
                                </td>
                              </tr>
                            ) : null}
                            {!transferLoading && transferIncoming.length === 0 ? (
                              <tr>
                                <td className="p-3 text-muted-foreground" colSpan={5}>
                                  Nu există transferuri primite.
                                </td>
                              </tr>
                            ) : null}
                            {transferIncomingPageRows.map((t) => {
                              const st = String(t.status || '').toLowerCase();
                              const badgeCls =
                                st === 'accepted'
                                  ? 'border-success/30 text-success bg-success/10'
                                  : st === 'rejected'
                                    ? 'border-error/30 text-error bg-error/10'
                                    : 'border-warning/30 text-warning bg-warning/10';
                              const badgeLabel = st === 'accepted' ? 'Acceptat' : st === 'rejected' ? 'Respins' : 'În așteptare';
                              return (
                                <tr key={String(t.id)} className="hover:bg-muted/30">
                                  <td className="p-3 text-xs text-muted-foreground">{t.created_at ? new Date(t.created_at).toLocaleString('ro-RO') : '—'}</td>
                                  <td className="p-3">
                                    <div className="font-medium text-foreground">{t.student_full_name}</div>
                                    <div className="text-xs text-muted-foreground">{t.student_email || '—'}</div>
                                  </td>
                                  <td className="p-3 text-xs text-muted-foreground">{t.from_school_name || `Școala #${String(t.from_school_id)}`}</td>
                                  <td className="p-3">
                                    <span className={`text-[11px] px-2 py-0.5 rounded-md border ${badgeCls}`}>{badgeLabel}</span>
                                    {t.assigned_classroom_name ? <div className="text-xs text-muted-foreground mt-1">Clasă: {t.assigned_classroom_name}</div> : null}
                                  </td>
                                  <td className="p-3 text-right">
                                    <div className="flex items-center justify-end gap-2 flex-wrap">
                                      <a href={getSecretariatTransferExportUrl(t.id)} target="_blank" rel="noreferrer">
                                        <Button variant="outline" size="sm" iconName="Download">
                                          Export JSON
                                        </Button>
                                      </a>
                                      <Button variant="outline" size="sm" iconName="Paperclip" onClick={() => void openTransferFiles(t)}>
                                        Fișiere
                                      </Button>
                                      {st === 'pending' ? (
                                        <>
                                          <Button variant="outline" size="sm" iconName="X" onClick={() => void submitTransferReject(t)} disabled={transferProcessBusy}>
                                            Respinge
                                          </Button>
                                          <Button size="sm" iconName="Check" onClick={() => openTransferAccept(t)} disabled={transferProcessBusy}>
                                            Acceptă
                                          </Button>
                                        </>
                                      ) : null}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 border border-border rounded-lg p-4 bg-background">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-sm font-semibold text-foreground">Transferuri trimise</div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setTransferOutgoingPage((p) => Math.max(1, p - 1))}
                          disabled={transferOutgoingPage <= 1}
                        >
                          Prev
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setTransferOutgoingPage((p) => Math.min(transferOutgoingTotalPages, p + 1))}
                          disabled={transferOutgoingPage >= transferOutgoingTotalPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>

                    <div className="mt-3 overflow-auto">
                      <table className="min-w-[760px] w-full text-sm">
                        <thead className="bg-muted/40">
                          <tr>
                            <th className="p-3 text-left text-muted-foreground font-medium">Data</th>
                            <th className="p-3 text-left text-muted-foreground font-medium">Elev</th>
                            <th className="p-3 text-left text-muted-foreground font-medium">Către școala</th>
                            <th className="p-3 text-left text-muted-foreground font-medium">Status</th>
                            <th className="p-3 text-right text-muted-foreground font-medium">Acțiuni</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {transferLoading && transferOutgoing.length === 0 ? (
                            <tr>
                              <td className="p-3 text-muted-foreground" colSpan={5}>
                                Se încarcă…
                              </td>
                            </tr>
                          ) : null}
                          {!transferLoading && transferOutgoing.length === 0 ? (
                            <tr>
                              <td className="p-3 text-muted-foreground" colSpan={5}>
                                Nu există transferuri trimise.
                              </td>
                            </tr>
                          ) : null}
                          {transferOutgoingPageRows.map((t) => {
                            const st = String(t.status || '').toLowerCase();
                            const badgeCls =
                              st === 'accepted'
                                ? 'border-success/30 text-success bg-success/10'
                                : st === 'rejected'
                                  ? 'border-error/30 text-error bg-error/10'
                                  : 'border-warning/30 text-warning bg-warning/10';
                            const badgeLabel = st === 'accepted' ? 'Acceptat' : st === 'rejected' ? 'Respins' : 'În așteptare';
                            return (
                              <tr key={String(t.id)} className="hover:bg-muted/30">
                                <td className="p-3 text-xs text-muted-foreground">{t.created_at ? new Date(t.created_at).toLocaleString('ro-RO') : '—'}</td>
                                <td className="p-3">
                                  <div className="font-medium text-foreground">{t.student_full_name}</div>
                                  <div className="text-xs text-muted-foreground">{t.student_email || '—'}</div>
                                </td>
                                <td className="p-3 text-xs text-muted-foreground">{t.to_school_name || `Școala #${String(t.to_school_id)}`}</td>
                                <td className="p-3">
                                  <span className={`text-[11px] px-2 py-0.5 rounded-md border ${badgeCls}`}>{badgeLabel}</span>
                                  {t.processed_at ? <div className="text-xs text-muted-foreground mt-1">Procesat: {new Date(t.processed_at).toLocaleString('ro-RO')}</div> : null}
                                </td>
                                <td className="p-3 text-right">
                                  <div className="flex items-center justify-end gap-2 flex-wrap">
                                    <a href={getSecretariatTransferExportUrl(t.id)} target="_blank" rel="noreferrer">
                                      <Button variant="outline" size="sm" iconName="Download">
                                        Export JSON
                                      </Button>
                                    </a>
                                    <Button variant="outline" size="sm" iconName="Paperclip" onClick={() => void openTransferFiles(t)}>
                                      Fișiere
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="mt-4 bg-muted/30 border border-border rounded-lg p-4">
                    <div className="text-sm font-semibold text-foreground">{'Ajutor: cum func\u021bioneaz\u0103 transferul'}</div>
                    <ul className="mt-2 text-sm text-muted-foreground space-y-1 list-disc pl-5">
                      <li>
                        {'Transferul trimite c\u0103tre alt\u0103 \u0219coal\u0103 un '}
                        <span className="text-foreground">{'export read-only'}</span>
                        {' cu datele elevului (note, absen\u021be, documente).'}
                      </li>
                      <li>{'\u0218coala de destina\u021bie prime\u0219te o notificare \u0219i poate Accepta sau Respinge transferul.'}</li>
                      <li>
                        {'La Acceptare, po\u021bi seta op\u021bional clasa din catalog (pentru eviden\u021b\u0103). '}
                        <span className="text-foreground">{'Nu'}</span>
                        {' se import\u0103 automat \u00een cataloagele \u0219colii de destina\u021bie (MVP).'}
                      </li>
                      <li>{'Oricare dintre \u0219coli poate desc\u0103rca exportul JSON \u0219i fi\u0219ierele ata\u0219ate din transfer.'}</li>
                    </ul>
                  </div>
                </div>
              ) : null}

              {tab === 'sheets' ? (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">Foi Google</h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        Linkuri utile pentru secretariat. Vizibile doar pentru conturile de secretariat din școala ta.
                      </p>
                    </div>
                    <Button variant="outline" iconName="RefreshCw" onClick={() => void loadSheets()} loading={sheetLinksLoading}>
                      Reîncarcă
                    </Button>
                  </div>

                  {sheetLinksError ? <div className="mt-3 text-sm text-error">{sheetLinksError}</div> : null}

                  <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-1 border border-border rounded-lg p-4 bg-background">
                      <div className="text-sm font-semibold text-foreground">Adaugă link</div>
                      <div className="text-xs text-muted-foreground mt-1">Ex: catalog, evidențe, rapoarte interne.</div>

                      {sheetCreateError ? (
                        <div className="mt-3 bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{sheetCreateError}</div>
                      ) : null}

                      <div className="mt-3 space-y-3">
                        <Input label="Titlu *" value={sheetTitle} onChange={(e) => setSheetTitle(e.target.value)} />
                        <Input
                          label="URL *"
                          placeholder="https://docs.google.com/spreadsheets/..."
                          value={sheetUrl}
                          onChange={(e) => setSheetUrl(e.target.value)}
                        />
                        <Button onClick={() => void submitCreateSheet()} loading={sheetCreateBusy} disabled={sheetCreateBusy}>
                          Adaugă
                        </Button>
                        <div className="text-xs text-muted-foreground">
                          Dacă vrei să îl și “încorporezi”, foaia trebuie “Publish to web” în Google Sheets. Aici păstrăm doar linkul.
                        </div>
                      </div>
                    </div>

                    <div className="lg:col-span-2 border border-border rounded-lg p-4 bg-background">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="text-sm font-semibold text-foreground">Linkuri</div>
                          <div className="text-xs text-muted-foreground">{sheetLinks.length}</div>
                        </div>
                      </div>

                      <div className="mt-3 space-y-2 max-h-[520px] overflow-auto pr-1">
                        {sheetLinksLoading && sheetLinks.length === 0 ? (
                          <div className="text-sm text-muted-foreground">Se încarcă…</div>
                        ) : null}
                        {!sheetLinksLoading && sheetLinks.length === 0 ? (
                          <div className="text-sm text-muted-foreground">Nu ai adăugat încă niciun link.</div>
                        ) : null}

                        {sheetLinks.map((r) => (
                          <div key={String(r.id)} className="border border-border rounded-lg p-3 bg-card">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-foreground truncate">{r.title}</div>
                                <div className="text-xs text-muted-foreground mt-1 truncate">{r.url}</div>
                                {r.created_at ? (
                                  <div className="text-[11px] text-muted-foreground mt-1">
                                    Adăugat: {new Date(r.created_at).toLocaleString()}
                                  </div>
                                ) : null}
                              </div>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                                <Button size="sm" variant="outline" onClick={() => window.open(r.url, '_blank')} className="w-full sm:w-auto">
                                  Deschide
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSheetEditError('');
                                    setSheetEditTarget(r);
                                    setSheetEditTitle(r.title);
                                    setSheetEditUrl(r.url);
                                    setSheetEditOpen(true);
                                  }}
                                  className="w-full sm:w-auto"
                                >
                                  Editează
                                </Button>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  loading={sheetDeleteBusyId === r.id}
                                  onClick={() => void submitDeleteSheet(r.id)}
                                  className="w-full sm:w-auto"
                                >
                                  Șterge
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {tab === 'registry' ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Înregistrare cerere</h2>
                        <p className="text-sm text-muted-foreground mt-1">Creează o cerere cu număr de registratură.</p>
                      </div>
                      <Button variant="outline" iconName="RefreshCw" onClick={() => void loadRegistry()} loading={registryLoading}>
                        Reîncarcă
                      </Button>
                    </div>

                    {registryError ? <div className="mt-3 text-sm text-error">{registryError}</div> : null}

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Select
                        label="Categorie"
                        value={registryForm.category}
                        options={[
                          { value: 'cerere', label: 'Cerere' },
                          { value: 'document', label: 'Document' },
                        ]}
                        onChange={(v) => {
                          const next = String(v || 'cerere');
                          setRegistryForm((p) => ({
                            ...p,
                            category: next,
                            direction: next === 'cerere' ? 'intrare' : p.direction || 'intrare',
                          }));
                        }}
                      />
                      <Select
                        label="Direcție"
                        value={registryForm.direction}
                        options={[
                          { value: 'intrare', label: 'Intrare' },
                          { value: 'iesire', label: 'Ieșire' },
                        ]}
                        onChange={(v) => setRegistryForm((p) => ({ ...p, direction: String(v || 'intrare') }))}
                      />
                      <Select
                        label="Tip cerere"
                        value={registryForm.request_type}
                        options={[
                          { value: 'cerere_generala', label: 'Cerere generală' },
                          { value: 'transfer', label: 'Transfer' },
                          { value: 'bursa', label: 'Bursă' },
                          { value: 'scutire_sport', label: 'Scutire sport' },
                          { value: 'eliberare_document', label: 'Eliberare document' },
                          { value: 'inregistrare_document', label: 'Înregistrare document' },
                        ]}
                        onChange={(v) => setRegistryForm((p) => ({ ...p, request_type: String(v || 'cerere_generala') }))}
                      />
                      <Select
                        label="Elev (opțional)"
                        value={String(registryForm.student_id || '')}
                        options={registryStudentOptions}
                        onChange={(v) => setRegistryForm((p) => ({ ...p, student_id: String(v || '') }))}
                        searchable
                        clearable
                      />
                      <Input
                        label="Subiect (opțional)"
                        value={registryForm.subject}
                        onChange={(e) => setRegistryForm((p) => ({ ...p, subject: e.target.value }))}
                      />
                      <Input
                        label="Nr. document (opțional)"
                        value={registryForm.doc_number}
                        onChange={(e) => setRegistryForm((p) => ({ ...p, doc_number: e.target.value }))}
                      />
                      <Input
                        label="Emitent (opțional)"
                        value={registryForm.issuer}
                        onChange={(e) => setRegistryForm((p) => ({ ...p, issuer: e.target.value }))}
                      />
                      <Input
                        label="Destinatar (opțional)"
                        value={registryForm.recipient}
                        onChange={(e) => setRegistryForm((p) => ({ ...p, recipient: e.target.value }))}
                      />
                      <Input
                        label="Nume solicitant (opțional)"
                        value={registryForm.requester_name}
                        onChange={(e) => setRegistryForm((p) => ({ ...p, requester_name: e.target.value }))}
                      />
                      <Input
                        label="Email solicitant (opțional)"
                        value={registryForm.requester_email}
                        onChange={(e) => setRegistryForm((p) => ({ ...p, requester_email: e.target.value }))}
                      />
                      <Input
                        label="Telefon solicitant (opțional)"
                        value={registryForm.requester_phone}
                        onChange={(e) => setRegistryForm((p) => ({ ...p, requester_phone: e.target.value }))}
                      />
                    </div>

                    <div className="mt-3">
                      <label className="block text-sm font-medium text-foreground mb-1">Descriere (opțional)</label>
                      <textarea
                        value={registryForm.description}
                        onChange={(e) => setRegistryForm((p) => ({ ...p, description: e.target.value }))}
                        className="w-full min-h-[110px] px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Detalii cerere..."
                      />
                    </div>

                    <div className="mt-4 flex items-center justify-end gap-2">
                      <Button onClick={() => void submitRegistry()} loading={registryBusy} iconName="Plus">
                        Înregistrează
                      </Button>
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">Se generează automat număr de registratură pe anul școlar curent.</div>
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Registratură</h2>
                        <p className="text-sm text-muted-foreground mt-1">Căutare + status.</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Live: actualizare automată la ~2.5s
                          {registryLastUpdatedAt
                            ? ` • Actualizat: ${new Date(registryLastUpdatedAt).toLocaleTimeString('ro-RO', {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                              })}`
                            : ''}
                        </p>
                      </div>
                      <Button variant="outline" iconName="RefreshCw" onClick={() => void loadRegistry()} loading={registryLoading}>
                        Reîncarcă
                      </Button>
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        label="Caută"
                        placeholder="Subiect / descriere / solicitant / elev"
                        value={registryQuery}
                        onChange={(e) => setRegistryQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void loadRegistry();
                        }}
                      />
                      <Select
                        label="Categorie"
                        value={registryCategory ?? ''}
                        options={[
                          { value: '', label: 'Toate' },
                          { value: 'cerere', label: 'Cereri' },
                          { value: 'document', label: 'Documente' },
                        ]}
                        onChange={(v) => setRegistryCategory(v ? String(v) : null)}
                        clearable
                      />
                      <Select
                        label="Direcție"
                        value={registryDirection ?? ''}
                        options={[
                          { value: '', label: 'Toate' },
                          { value: 'intrare', label: 'Intrări' },
                          { value: 'iesire', label: 'Ieșiri' },
                        ]}
                        onChange={(v) => setRegistryDirection(v ? String(v) : null)}
                        clearable
                      />
                      <Select
                        label="Status"
                        value={registryStatus ?? ''}
                        options={[
                          { value: '', label: 'Toate' },
                          { value: 'open', label: 'Deschis' },
                          { value: 'in_progress', label: 'În lucru' },
                          { value: 'done', label: 'Finalizat' },
                          { value: 'canceled', label: 'Anulat' },
                        ]}
                        onChange={(v) => setRegistryStatus(v ? String(v) : null)}
                        clearable
                      />
                    </div>

                    <div className="mt-4 space-y-2 max-h-[520px] overflow-auto pr-1">
                      {registryLoading && registryRows.length === 0 ? (
                        <div className="text-sm text-muted-foreground">Se încarcă…</div>
                      ) : null}
                      {!registryLoading && registryRows.length === 0 ? (
                        <div className="text-sm text-muted-foreground">Nu există înregistrări.</div>
                      ) : null}
                      {registryRows.map((r) => (
                        <div key={String(r.id)} className="border border-border rounded-lg p-3 bg-background">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-foreground truncate">
                                #{r.registry_number}/{r.registry_year} • {r.category}/{r.direction} • {r.request_type}
                              </div>
                              <div className="text-xs text-muted-foreground mt-1 truncate">{r.subject || r.description || '—'}</div>
                              {r.doc_number || r.issuer || r.recipient ? (
                                <div className="text-xs text-muted-foreground mt-1 truncate">
                                  {r.doc_number ? `Nr: ${r.doc_number}` : ''} {r.issuer ? ` • Emitent: ${r.issuer}` : ''}{' '}
                                  {r.recipient ? ` • Destinatar: ${r.recipient}` : ''}
                                </div>
                              ) : null}
                              <div className="text-xs text-muted-foreground mt-1 truncate">
                                {r.student_name ? `Elev: ${r.student_name}` : ''}{' '}
                                {r.requester_name ? ` • Solicitant: ${r.requester_name}` : ''}
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                              <select
                                value={r.status}
                                onChange={(e) => void setRegistryRowStatus(r.id, e.target.value)}
                                className="h-9 px-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary w-full sm:min-w-[140px]"
                              >
                                <option value="open">Deschis</option>
                                <option value="in_progress">În lucru</option>
                                <option value="done">Finalizat</option>
                                <option value="canceled">Anulat</option>
                              </select>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const next = registryOpenId === r.id ? null : r.id;
                                  setRegistryOpenId(next);
                                  if (next) void loadRegistryAttachments(next);
                                }}
                              >
                                Atașamente
                              </Button>
                            </div>
                          </div>

                          {registryOpenId === r.id ? (
                            <div className="mt-3 border-t border-border pt-3">
                              {registryAttError ? <div className="mb-2 text-sm text-error">{registryAttError}</div> : null}
                              <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                                <div className="w-full sm:min-w-[240px] flex-1">
                                  <Input
                                    label="Titlu fișier (opțional)"
                                    value={registryAttUploadTitle}
                                    onChange={(e) => setRegistryAttUploadTitle(e.target.value)}
                                  />
                                </div>
                                <div className="pb-[2px] w-full sm:w-auto">
                                  <input
                                    type="file"
                                    disabled={registryAttUploadBusy}
                                    className="w-full"
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      if (!f) return;
                                      void uploadRegistryAttachment(r.id, f);
                                      e.currentTarget.value = '';
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="mt-2 text-xs text-muted-foreground">Poți încărca mai multe fișiere. Max 10MB/fișier.</div>

                              <div className="mt-3 space-y-2">
                                {registryAttLoadingId === r.id ? (
                                  <div className="text-sm text-muted-foreground">Se încarcă…</div>
                                ) : null}
                                {(registryAttByEntry[r.id] || []).length === 0 && registryAttLoadingId !== r.id ? (
                                  <div className="text-sm text-muted-foreground">Nu există atașamente.</div>
                                ) : null}
                                {(registryAttByEntry[r.id] || []).map((a) => (
                                  <div key={String(a.id)} className="border border-border rounded-lg p-2 bg-background flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-medium text-foreground truncate">{a.title || a.original_name}</div>
                                      <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                        {a.created_at ? new Date(a.created_at).toLocaleString() : ''}
                                      </div>
                                    </div>
                                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => window.open(getSecretariatRegistryAttachmentDownloadUrl(a.id), '_blank')} className="w-full sm:w-auto"
                                      >
                                        Descarcă
                                      </Button>
                                      <Button size="sm" variant="danger" onClick={() => void deleteRegistryAttachment(r.id, a.id)} className="w-full sm:w-auto">
                                        Șterge
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}

              {tab === 'reports' ? (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">Rapoarte</h2>
                      <p className="text-sm text-muted-foreground mt-1">Export CSV (Excel/Google Sheets).</p>
                    </div>
                    <Button variant="outline" iconName="RefreshCw" onClick={() => void loadReportYears()} loading={reportsYearsLoading}>
                      Reîncarcă
                    </Button>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Select
                      label="An școlar"
                      value={reportsSchoolYear}
                      options={(reportsYears.length ? reportsYears : ['']).map((y) => ({ value: String(y), label: String(y || '—') }))}
                      onChange={(v) => setReportsSchoolYear(String(v || ''))}
                    />
                    <Select
                      label="Clasă (opțional)"
                      value={reportsClassroomId ?? ''}
                      options={[
                        { value: '', label: 'Toate clasele' },
                        ...(classes || []).map((c) => ({ value: Number(c.classroom_id), label: String(c.name || `Clasa #${c.classroom_id}`) })),
                      ]}
                      onChange={(v) => {
                        const n = Number(v);
                        setReportsClassroomId(Number.isFinite(n) && n > 0 ? n : null);
                      }}
                      clearable
                    />
                    <Input
                      label="Caută elev (opțional)"
                      placeholder="Nume / email"
                      value={reportsStudentsQuery}
                      onChange={(e) => setReportsStudentsQuery(e.target.value)}
                    />
                  </div>

                  {reportsYearsError ? <div className="mt-3 text-sm text-error">{reportsYearsError}</div> : null}
                  {reportsDownloadError ? <div className="mt-3 text-sm text-error">{reportsDownloadError}</div> : null}

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="border border-border rounded-lg p-4 bg-background">
                      <div className="text-sm font-semibold text-foreground">Lista elevi</div>
                      <div className="text-xs text-muted-foreground mt-1">Include clasa, contact, 16+.</div>
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          iconName="Download"
                          loading={reportsDownloadBusy}
                          onClick={() => {
                            const url = getSecretariatReportStudentsCsvUrl({
                              q: reportsStudentsQuery.trim() || undefined,
                              classroom_id: reportsClassroomId,
                            });
                            void downloadReportCsv(url, 'raport_elevi.csv');
                          }}
                        >
                          Descarcă CSV
                        </Button>
                      </div>
                    </div>

                    <div className="border border-border rounded-lg p-4 bg-background">
                      <div className="text-sm font-semibold text-foreground">Absente (sumar)</div>
                      <div className="text-xs text-muted-foreground mt-1">Total / motivate / nemotivate pe elev.</div>
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          iconName="Download"
                          loading={reportsDownloadBusy}
                          onClick={() => {
                            const url = getSecretariatReportAbsencesCsvUrl({
                              school_year: reportsSchoolYear || null,
                              classroom_id: reportsClassroomId,
                            });
                            const year = (reportsSchoolYear || 'raport').trim() || 'raport';
                            void downloadReportCsv(url, `raport_absente_${year}.csv`);
                          }}
                        >
                          Descarcă CSV
                        </Button>
                      </div>
                    </div>

                    <div className="border border-border rounded-lg p-4 bg-background">
                      <div className="text-sm font-semibold text-foreground">Note (export)</div>
                      <div className="text-xs text-muted-foreground mt-1">Export rânduri note; filtrare după an/clasă.</div>
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          iconName="Download"
                          loading={reportsDownloadBusy}
                          onClick={() => {
                            const url = getSecretariatReportGradesCsvUrl({
                              school_year: reportsSchoolYear || null,
                              classroom_id: reportsClassroomId,
                            });
                            const year = (reportsSchoolYear || 'raport').trim() || 'raport';
                            void downloadReportCsv(url, `raport_note_${year}.csv`);
                          }}
                        >
                          Descarcă CSV
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {tab !== 'overview' &&
              tab !== 'classes' &&
              tab !== 'archive' &&
              tab !== 'requests' &&
              tab !== 'users' &&
              tab !== 'students' &&
              tab !== 'transfers' &&
              tab !== 'sheets' &&
              tab !== 'registry' &&
              tab !== 'reports' ? (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="text-sm text-muted-foreground">Secțiune în lucru.</div>
                </div>
              ) : null}
            </div>
          </main>
        </div>
      </div>

      {sheetEditOpen ? (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-lg">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="text-lg font-semibold text-foreground">Editează link</div>
              <button
                className="p-2 rounded-md hover:bg-muted"
                onClick={() => {
                  setSheetEditOpen(false);
                  setSheetEditTarget(null);
                }}
                aria-label="Închide"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {sheetEditError ? (
                <div className="bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{sheetEditError}</div>
              ) : null}
              <Input label="Titlu *" value={sheetEditTitle} onChange={(e) => setSheetEditTitle(e.target.value)} />
              <Input
                label="URL *"
                placeholder="https://docs.google.com/spreadsheets/..."
                value={sheetEditUrl}
                onChange={(e) => setSheetEditUrl(e.target.value)}
              />
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSheetEditOpen(false);
                    setSheetEditTarget(null);
                  }}
                  disabled={sheetEditBusy}
                >
                  Anulează
                </Button>
                <Button onClick={() => void submitUpdateSheet()} loading={sheetEditBusy} disabled={sheetEditBusy}>
                  Salvează
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showAddTeacherModal ? (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-lg">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="text-lg font-semibold text-foreground">Adaugă profesor</div>
              <button className="p-2 rounded-md hover:bg-muted" onClick={() => setShowAddTeacherModal(false)} aria-label="Închide">
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <Input
                label="Nume *"
                value={teacherForm.full_name}
                onChange={(e) => setTeacherForm((p) => ({ ...p, full_name: e.target.value }))}
              />
              <Input
                label="Email *"
                type="email"
                value={teacherForm.email}
                onChange={(e) => setTeacherForm((p) => ({ ...p, email: e.target.value }))}
              />
              <Input
                label="Telefon (opțional)"
                value={teacherForm.phone}
                onChange={(e) => setTeacherForm((p) => ({ ...p, phone: e.target.value }))}
              />
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowAddTeacherModal(false)}>
                  Anulează
                </Button>
                <Button onClick={() => void submitCreateTeacher()} loading={teacherBusy}>
                  Creează
                </Button>
              </div>
              <div className="text-xs text-muted-foreground">
                Parola temporară se trimite pe email, iar la primul login parola trebuie schimbată.
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showAddStudentModal ? (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
              <div className="text-lg font-semibold text-foreground">Adaugă elev</div>
              <button className="p-2 rounded-md hover:bg-muted" onClick={() => setShowAddStudentModal(false)} aria-label="Închide">
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {formError ? <div className="bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{formError}</div> : null}

              <Input label="Nume elev *" value={studentForm.name} onChange={(e) => setStudentForm((p) => ({ ...p, name: e.target.value }))} />
              <Input
                label="Data nașterii (opțional)"
                type="date"
                value={studentForm.birthDate}
                onChange={(e) => setStudentForm((p) => ({ ...p, birthDate: e.target.value }))}
              />

              {!studentForm.birthDate ? (
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={studentForm.manualOver16}
                    onChange={(e) => setStudentForm((p) => ({ ...p, manualOver16: e.target.checked }))}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span>Elevul are cel puțin 16 ani</span>
                </label>
              ) : null}

              {isOver16 ? (
                <>
                  <Input
                    label="Email elev *"
                    type="email"
                    value={studentForm.studentEmail}
                    onChange={(e) => setStudentForm((p) => ({ ...p, studentEmail: e.target.value }))}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Telefon elev (opțional)"
                      value={studentForm.studentPhone}
                      onChange={(e) => setStudentForm((p) => ({ ...p, studentPhone: e.target.value }))}
                    />
                    <Input
                      label="Adresă reședință (opțional)"
                      value={studentForm.studentAddress}
                      onChange={(e) => setStudentForm((p) => ({ ...p, studentAddress: e.target.value }))}
                    />
                  </div>

                  <div className="border-t border-border pt-3">
                    <div className="text-sm font-medium text-foreground mb-1">Părinți (opțional)</div>
                    <div className="text-xs text-muted-foreground mb-3">
                      Dacă adaugi emailul părintelui, se creează un cont de părinte pentru vizualizarea evoluției elevului.
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <Input
                          label="Nume părinte (mamă) (opțional)"
                          value={studentForm.parentName1}
                          onChange={(e) => setStudentForm((p) => ({ ...p, parentName1: e.target.value }))}
                        />
                        <Input
                          label="Email părinte (mamă) (opțional)"
                          type="email"
                          value={studentForm.parentEmail1}
                          onChange={(e) => setStudentForm((p) => ({ ...p, parentEmail1: e.target.value }))}
                        />
                        <Input
                          label="Telefon părinte (mamă) (opțional)"
                          value={studentForm.parentPhone1}
                          onChange={(e) => setStudentForm((p) => ({ ...p, parentPhone1: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-3">
                        <Input
                          label="Nume părinte (tată) (opțional)"
                          value={studentForm.parentName2}
                          onChange={(e) => setStudentForm((p) => ({ ...p, parentName2: e.target.value }))}
                        />
                        <Input
                          label="Email părinte (tată) (opțional)"
                          type="email"
                          value={studentForm.parentEmail2}
                          onChange={(e) => setStudentForm((p) => ({ ...p, parentEmail2: e.target.value }))}
                        />
                        <Input
                          label="Telefon părinte (tată) (opțional)"
                          value={studentForm.parentPhone2}
                          onChange={(e) => setStudentForm((p) => ({ ...p, parentPhone2: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="border-t border-border pt-3">
                  <div className="text-sm font-medium text-foreground mb-1">Părinți *</div>
                  <div className="text-xs text-muted-foreground mb-3">
                    Pentru elevii sub 16 ani, emailul unuia dintre părinți este obligatoriu (se creează cont de părinte).
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <Input
                        label="Nume părinte (mamă)"
                        value={studentForm.parentName1}
                        onChange={(e) => setStudentForm((p) => ({ ...p, parentName1: e.target.value }))}
                      />
                      <Input
                        label="Email părinte (mamă)"
                        type="email"
                        value={studentForm.parentEmail1}
                        onChange={(e) => setStudentForm((p) => ({ ...p, parentEmail1: e.target.value }))}
                      />
                      <Input
                        label="Telefon părinte (mamă) (opțional)"
                        value={studentForm.parentPhone1}
                        onChange={(e) => setStudentForm((p) => ({ ...p, parentPhone1: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-3">
                      <Input
                        label="Nume părinte (tată)"
                        value={studentForm.parentName2}
                        onChange={(e) => setStudentForm((p) => ({ ...p, parentName2: e.target.value }))}
                      />
                      <Input
                        label="Email părinte (tată)"
                        type="email"
                        value={studentForm.parentEmail2}
                        onChange={(e) => setStudentForm((p) => ({ ...p, parentEmail2: e.target.value }))}
                      />
                      <Input
                        label="Telefon părinte (tată) (opțional)"
                        value={studentForm.parentPhone2}
                        onChange={(e) => setStudentForm((p) => ({ ...p, parentPhone2: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-3">
                    Dacă completezi emailul părintelui, se creează automat un cont de părinte (dacă nu există).
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowAddStudentModal(false)}>
                  Anulează
                </Button>
                <Button onClick={() => void submitAddStudent()} loading={formBusy}>
                  Salvează
                </Button>
              </div>
              <div className="text-xs text-muted-foreground">
                Parolele sunt trimise pe email și se schimbă obligatoriu la primul login (elev 16+ / părinți).
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {renameOpen ? (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-lg">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="text-lg font-semibold text-foreground">Redenumește clasa</div>
              <button
                className="p-2 rounded-md hover:bg-muted"
                onClick={() => {
                  setRenameOpen(false);
                  setRenameTarget(null);
                }}
                aria-label="Închide"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {renameError ? (
                <div className="bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{renameError}</div>
              ) : null}
              <Input label="Nume clasă" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} />
              <label className="flex items-start gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={archiveOnRename}
                  onChange={(e) => setArchiveOnRename(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-border text-primary focus:ring-primary"
                />
                <span>
                  Arhivează datele existente (blochează cataloagele curente ca <span className="font-medium">read-only</span>).
                </span>
              </label>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setRenameOpen(false);
                    setRenameTarget(null);
                  }}
                >
                  Anulează
                </Button>
                <Button onClick={() => void submitRenameClass()} loading={renameBusy}>
                  Salvează
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {archiveOpen ? (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-lg">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="text-lg font-semibold text-foreground">Arhivează clasa</div>
              <button
                className="p-2 rounded-md hover:bg-muted"
                onClick={() => {
                  setArchiveOpen(false);
                  setArchiveTarget(null);
                }}
                aria-label="Închide"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {archiveError ? (
                <div className="bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{archiveError}</div>
              ) : null}
              <div className="text-sm text-foreground">
                Confirmi arhivarea clasei <span className="font-medium">{archiveTarget?.name || '—'}</span>?
              </div>
              <div className="text-sm text-muted-foreground">
                Clasa va fi mutată în <span className="font-medium">Arhivă</span>, iar cataloagele curente vor deveni{' '}
                <span className="font-medium">read-only</span>.
              </div>
              <div className="text-sm text-error">
                Atenție: această acțiune este <span className="font-medium">ireversibilă</span>.
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setArchiveOpen(false);
                    setArchiveTarget(null);
                  }}
                >
                  Anulează
                </Button>
                <Button variant="danger" onClick={() => void submitArchiveClass()} loading={archiveBusy}>
                  Arhivează
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {transferProcessOpen && transferProcessTarget ? (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg bg-card border border-border rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">Procesează transfer</h2>
            <div className="text-sm text-muted-foreground">
              Elev: <span className="text-foreground font-medium">{transferProcessTarget.student_full_name}</span>
              {transferProcessTarget.student_email ? <span className="text-muted-foreground"> • {transferProcessTarget.student_email}</span> : null}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Din: {transferProcessTarget.from_school_name || `Școala #${String(transferProcessTarget.from_school_id)}`}
            </div>

            {transferProcessError ? <div className="mt-3 text-sm text-error">{transferProcessError}</div> : null}

            <div className="mt-4 space-y-3">
              <Select
                label="Clasă (opțional)"
                value={transferAssignClassId ?? ''}
                options={transferClassOptions}
                onChange={(v) => setTransferAssignClassId(v ? Number(v) : null)}
                searchable
                clearable
                disabled={transferClassesLoading}
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Mesaj (opțional)</label>
                <textarea
                  value={transferProcessMessage}
                  onChange={(e) => setTransferProcessMessage(e.target.value)}
                  className="w-full min-h-[90px] px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Ex: Elevul a fost repartizat în clasa…"
                />
              </div>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={() => {
                  setTransferProcessOpen(false);
                  setTransferProcessTarget(null);
                }}
                disabled={transferProcessBusy}
              >
                Închide
              </Button>
              <Button
                variant="outline"
                iconName="X"
                onClick={() => void submitTransferReject(transferProcessTarget)}
                loading={transferProcessBusy}
                disabled={transferProcessBusy}
              >
                Respinge
              </Button>
              <Button iconName="Check" onClick={() => void submitTransferAccept()} loading={transferProcessBusy} disabled={transferProcessBusy}>
                Acceptă
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {transferFilesOpen && transferFilesTarget ? (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg bg-card border border-border rounded-lg shadow-lg p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-1">Fișiere transfer</h2>
                <div className="text-sm text-muted-foreground">
                  {transferFilesTarget.student_full_name} • #{String(transferFilesTarget.id)}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setTransferFilesOpen(false);
                  setTransferFilesTarget(null);
                }}
              >
                Închide
              </Button>
            </div>

            {transferFilesError ? <div className="mt-3 text-sm text-error">{transferFilesError}</div> : null}

            <div className="mt-4 space-y-2 max-h-[420px] overflow-auto pr-1">
              {transferFilesLoading ? <div className="text-sm text-muted-foreground">Se încarcă…</div> : null}
              {!transferFilesLoading && transferFiles.length === 0 ? (
                <div className="text-sm text-muted-foreground">Nu există fișiere în acest transfer.</div>
              ) : null}
              {transferFiles.map((f) => (
                <div key={String(f.id)} className="border border-border rounded-lg p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{f.original_name}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {(f.size ?? null) !== null ? `${String(f.size)} bytes` : '—'}
                      {f.created_at ? <span> • {new Date(f.created_at).toLocaleString('ro-RO')}</span> : null}
                    </div>
                  </div>
                  <a href={getSecretariatTransferFileDownloadUrl(f.id)} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm" iconName="Download">
                      Descarcă
                    </Button>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {showPwdModal && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md bg-card border border-border rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">Schimbă parola inițială</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Pentru securitate, te rugăm să îți setezi o parolă nouă.
            </p>

            {pwdError && <div className="mb-3 text-sm text-error">{pwdError}</div>}

            <div className="space-y-3">
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Parola nouă"
                autoComplete="new-password"
              />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Confirmă parola"
                autoComplete="new-password"
              />
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  signOut();
                  navigate('/authentication-portal');
                }}
              >
                Delogare
              </Button>
              <Button variant="default" onClick={handleChangePassword} loading={pwdLoading}>
                Salvează
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
